import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { 
  Database, 
  Server, 
  RefreshCw, 
  Table, 
  CheckCircle, 
  XCircle,
  Shield,
  Activity,
  Menu,
  X,
  GitBranch,
  Key,
  ArrowRight,
  ArrowLeft,
  Info,
  Play,
  Save,
  Trash2,
  Code,
  Mail,
  MapPin,
  Briefcase,
  Filter,
  Target,
  User,
  Edit,
  Plus,
  Settings,
  BarChart2,
  MousePointerClick,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Users,
  LogOut,
  TrendingUp,
  Send,
  Eye,
  Hash
} from 'lucide-react';
import './hcapp.css';
import './App.css';
import packageJson from '../package.json';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage from './LoginPage';

function App() {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [serverStatus, setServerStatus] = useState(null);
  const [vpnStatus, setVpnStatus] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const [mailingDbStatus, setMailingDbStatus] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [relationships, setRelationships] = useState({});
  const [tableInfo, setTableInfo] = useState(null);
  const [tableFilter, setTableFilter] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({});
  const [queryEditor, setQueryEditor] = useState('');
  const [queryResults, setQueryResults] = useState([]);
  const [savedQueries, setSavedQueries] = useState([]);
  const [mailingOrders, setMailingOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [filterType, setFilterType] = useState('radius');
  const [radiusFilter, setRadiusFilter] = useState(50);
  const [travelTimeFilter, setTravelTimeFilter] = useState(60);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  const [emailContent, setEmailContent] = useState(null);
  const [daysFilter, setDaysFilter] = useState(14);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailGenerationProgress, setEmailGenerationProgress] = useState(null);
  const [editableEmailBody, setEditableEmailBody] = useState('');
  const [editableEmailSubject, setEditableEmailSubject] = useState('');
  const [editablePreviewText, setEditablePreviewText] = useState('');
  const [contactPersons, setContactPersons] = useState([]);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);
  const [showContactManagement, setShowContactManagement] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [hubspotAutoSend, setHubspotAutoSend] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [testRecipients, setTestRecipients] = useState([]);
  const [selectedTestRecipients, setSelectedTestRecipients] = useState([]);
  const [emailMode, setEmailMode] = useState('production'); // 'production' or 'test'
  const [trackingData, setTrackingData] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [dbStatusChecked, setDbStatusChecked] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [llmPrompt, setLlmPrompt] = useState('');
  const [llmPromptLoading, setLlmPromptLoading] = useState(false);
  const [llmPromptSaving, setLlmPromptSaving] = useState(false);
  const [llmPromptIsDefault, setLlmPromptIsDefault] = useState(false);
  const [subjectPrompt, setSubjectPrompt] = useState('');
  const [subjectPromptLoading, setSubjectPromptLoading] = useState(false);
  const [subjectPromptSaving, setSubjectPromptSaving] = useState(false);
  const [subjectPromptIsDefault, setSubjectPromptIsDefault] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [previewPromptLoading, setPreviewPromptLoading] = useState(false);
  const [previewPromptSaving, setPreviewPromptSaving] = useState(false);
  const [previewPromptIsDefault, setPreviewPromptIsDefault] = useState(false);
  const [mailingHistory, setMailingHistory] = useState([]);
  const [showMailingChoice, setShowMailingChoice] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showMailingHistory, setShowMailingHistory] = useState(false);
  const [resendCampaignData, setResendCampaignData] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [allTrades, setAllTrades] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradeSearchQuery, setTradeSearchQuery] = useState('');
  const [tradesDropdownOpen, setTradesDropdownOpen] = useState(false);

  const loadFromLocalStorage = () => {
    const stored = localStorage.getItem('dbAppSettings');
    if (stored) {
      try {
        const settings = JSON.parse(stored);
        if (settings.visibleColumns) setVisibleColumns(settings.visibleColumns);
        if (settings.savedQueries) setSavedQueries(settings.savedQueries);
        if (settings.tableFilter) setTableFilter(settings.tableFilter);
        if (settings.hubspotAutoSend !== undefined) setHubspotAutoSend(settings.hubspotAutoSend);
      } catch (error) {
        console.error('Fehler beim Laden der Einstellungen:', error);
      }
    }
  };

  const saveToLocalStorage = useCallback(() => {
    const settings = {
      visibleColumns,
      savedQueries,
      tableFilter,
      hubspotAutoSend
    };
    localStorage.setItem('dbAppSettings', JSON.stringify(settings));
  }, [visibleColumns, savedQueries, tableFilter, hubspotAutoSend]);

  const loadDashboardStats = async () => {
    setDashboardLoading(true);
    try {
      const response = await axios.get('/api/dashboard/stats');
      if (response.data.success) {
        setDashboardStats(response.data.stats);
      }
    } catch (error) {
      console.error('[DASHBOARD] Error loading stats:', error);
    }
    setDashboardLoading(false);
  };

  useEffect(() => {
    checkServerStatus();
    checkVpnStatus();
    checkDatabaseStatus();
    loadFromLocalStorage();
    loadContactPersons();
    loadDashboardStats();
  }, []);

  useEffect(() => {
    if (activeView === 'settings') {
      loadLlmPrompt();
      loadSubjectPrompt();
      loadPreviewPrompt();
    }
    if (activeView === 'contacts') {
      loadContactPersons();
    }
    if (activeView === 'tracking') {
      loadTrackingOverview();
    }
  }, [activeView]);

  useEffect(() => {
    saveToLocalStorage();
  }, [visibleColumns, savedQueries, tableFilter, hubspotAutoSend, saveToLocalStorage]);

  const checkServerStatus = async () => {
    try {
      const response = await axios.get('/api/health');
      setServerStatus(response.data);
    } catch (error) {
      setServerStatus({ status: 'error', message: 'Server nicht erreichbar' });
    }
  };

  const checkVpnStatus = async () => {
    try {
      const response = await axios.get('/api/vpn/status');
      setVpnStatus(response.data);
    } catch (error) {
      setVpnStatus({ configured: false, connected: false, installed: false, error: error.message });
    }
  };

  const connectVpn = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/vpn/connect');
      if (response.data.success) {
        await checkVpnStatus();
      }
      toast.success(response.data.message);
    } catch (error) {
      toast.error('VPN-Verbindung fehlgeschlagen: ' + (error.response?.data?.message || error.message));
    }
    setLoading(false);
  };

  const disconnectVpn = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/vpn/disconnect');
      if (response.data.success) {
        await checkVpnStatus();
      }
      toast.success(response.data.message);
    } catch (error) {
      toast.error('VPN-Trennung fehlgeschlagen: ' + (error.response?.data?.message || error.message));
    }
    setLoading(false);
  };

  const checkDatabaseStatus = async (force = false) => {
    // Nur beim ersten Mal oder wenn force=true
    if (dbStatusChecked && !force) {
      return;
    }

    try {
      console.log('[DB-CHECK] Prüfe Datenbankverbindungen...');
      
      const [appDbResponse, mailingDbResponse] = await Promise.all([
        axios.get('/api/database/test').catch(err => {
          console.error('[DB-CHECK] App-DB Fehler:', err.response?.data || err.message);
          return { 
            data: { 
              success: false, 
              message: err.response?.data?.message || err.message,
              database: 'Fehler'
            } 
          };
        }),
        axios.get('/api/database/test-mailing').catch(err => {
          console.error('[DB-CHECK] Mailing-DB Fehler:', err.response?.data || err.message);
          return { 
            data: { 
              success: false, 
              message: err.response?.data?.message || err.message,
              database: 'Fehler'
            } 
          };
        })
      ]);
      
      console.log('[DB-CHECK] App-DB Status:', appDbResponse.data);
      console.log('[DB-CHECK] Mailing-DB Status:', mailingDbResponse.data);
      
      setDbStatus(appDbResponse.data);
      setMailingDbStatus(mailingDbResponse.data);
      setDbStatusChecked(true);

      // Zeige Toast bei Fehlern
      if (!appDbResponse.data.success) {
        toast.error(`App-DB Verbindungsfehler: ${appDbResponse.data.message}`);
      }
      if (!mailingDbResponse.data.success) {
        toast.error(`Mailing-DB Verbindungsfehler: ${mailingDbResponse.data.message}`);
      }
      
      // Zeige Erfolg nur beim ersten Check
      if (!dbStatusChecked && appDbResponse.data.success && mailingDbResponse.data.success) {
        toast.success('Beide Datenbankverbindungen erfolgreich hergestellt');
      }
    } catch (error) {
      console.error('[DB-CHECK] Fehler beim Prüfen der Datenbankverbindungen:', error);
      toast.error('Fehler beim Prüfen der Datenbankverbindungen');
    }
  };

  const testDatabaseConnection = async () => {
    setLoading(true);
    await checkDatabaseStatus();
    setLoading(false);
  };

  const loadTables = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/database/tables');
      setTables(response.data.tables);
      // DB-Status wird nur beim Start geprüft, nicht bei jedem Request
    } catch (error) {
      console.error('Fehler beim Laden der Tabellen:', error);
      // Bei Fehler: DB-Status neu prüfen
      await checkDatabaseStatus(true);
    }
    setLoading(false);
  };

  const loadTableData = async (tableName) => {
    setLoading(true);
    setSelectedTable(tableName);
    try {
      const response = await axios.get(`/api/database/data/${tableName}`);
      setTableData(response.data.data);
      
      if (response.data.data.length > 0 && !visibleColumns[tableName]) {
        const allColumns = Object.keys(response.data.data[0]);
        setVisibleColumns(prev => ({
          ...prev,
          [tableName]: allColumns.reduce((acc, col) => ({ ...acc, [col]: true }), {})
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
      setTableData([]);
    }
    setLoading(false);
  };

  const executeQuery = async () => {
    if (!queryEditor.trim()) {
      toast.error('Bitte geben Sie eine SQL-Query ein');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`/api/database/query?sql=${encodeURIComponent(queryEditor)}`);
      setQueryResults(response.data.data);
    } catch (error) {
      toast.error('Query-Fehler: ' + (error.response?.data?.error || error.message));
      setQueryResults([]);
    }
    setLoading(false);
  };

  const saveQuery = () => {
    const name = prompt('Name für die Query:');
    if (name && queryEditor.trim()) {
      setSavedQueries(prev => [...prev, { name, query: queryEditor, date: new Date().toISOString() }]);
    }
  };

  const loadQuery = (query) => {
    setQueryEditor(query);
  };

  const deleteQuery = (index) => {
    setSavedQueries(prev => prev.filter((_, i) => i !== index));
  };

  const toggleColumn = (table, column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [table]: {
        ...prev[table],
        [column]: !prev[table]?.[column]
      }
    }));
  };

  const toggleAllColumns = (table, visible) => {
    if (tableData.length > 0) {
      const allColumns = Object.keys(tableData[0]);
      setVisibleColumns(prev => ({
        ...prev,
        [table]: allColumns.reduce((acc, col) => ({ ...acc, [col]: visible }), {})
      }));
    }
  };

  const loadRelationships = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/database/relationships');
      setRelationships(response.data.relationships);
    } catch (error) {
      console.error('Fehler beim Laden der Beziehungen:', error);
    }
    setLoading(false);
  };

  const loadTableInfo = async (tableName) => {
    setLoading(true);
    setSelectedTable(tableName);
    try {
      const response = await axios.get(`/api/database/table/${tableName}/info`);
      setTableInfo(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Tabelleninfos:', error);
      setTableInfo(null);
    }
    setLoading(false);
  };

  const openSendDialog = async () => {
    await loadTestRecipients();
    setShowSendDialog(true);
  };

  const sendTestEmail = async () => {
    setConfirmDialog({
      title: '🧪 Test-E-Mail senden',
      message: `Möchten Sie die Test-E-Mail an ${selectedTestRecipients.length} Test-Empfänger senden?\n\nBetreff: ${editableEmailSubject}`,
      onConfirm: () => {
        setConfirmDialog(null);
        executeSendTestEmail();
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const executeSendTestEmail = async () => {

    setSendingEmail(true);
    setSendProgress(`Sende Test-E-Mail an ${selectedTestRecipients.length} Test-Empfänger...`);
    setSendResult(null);

    try {
      const response = await axios.post('/api/mailgun/send-test', {
        testRecipientIds: selectedTestRecipients,
        htmlBody: editableEmailBody,
        subject: `[TEST] ${editableEmailSubject}`,
        previewText: editablePreviewText,
        projectData: {
          id: selectedOrder,
          title: emailContent.orderDetails?.title || ''
        }
      });

      setSendResult(response.data);
      setSendProgress(null);

      if (response.data.success) {
        const recipients = response.data.contactPersons || response.data.testRecipients || [];
        toast.success(`Test-E-Mail erfolgreich versendet! Erfolgreich: ${response.data.successful}, Fehlgeschlagen: ${response.data.failed}`);
      }
    } catch (error) {
      console.error('[FRONTEND] Test send failed:', error);
      setSendProgress(null);
      setSendResult({ success: false, error: error.response?.data?.error || error.message });
      toast.error('Fehler beim Test-Versand: ' + (error.response?.data?.message || error.message));
    }

    setSendingEmail(false);
  };

  const sendProductionEmail = async () => {
    if (!emailContent || !editableEmailBody || !editableEmailSubject) {
      toast.error('Keine E-Mail-Daten vorhanden');
      return;
    }

    const recipientCount = emailContent.recipients.length;
    setConfirmDialog({
      title: '⚠️ ACHTUNG: Produktions-Versand',
      message: `Möchten Sie diese E-Mail wirklich an ${recipientCount} echte Empfänger versenden?\n\nBetreff: ${editableEmailSubject}\n\nDieser Vorgang kann nicht rückgängig gemacht werden!`,
      danger: true,
      onConfirm: () => {
        setConfirmDialog(null);
        executeSendProductionEmail();
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const executeSendProductionEmail = async () => {
    const recipientCount = emailContent.recipients.length;

    setSendingEmail(true);
    setSendProgress(`Sende E-Mail an ${recipientCount} Empfänger...`);
    setSendResult(null);
    setShowSendDialog(false);

    try {
      const response = await axios.post('/api/mailgun/send-bulk', {
        recipients: emailContent.recipients,
        projectData: {
          id: selectedOrder,
          title: emailContent.orderDetails?.title || '',
          description: emailContent.orderDetails?.description || '',
          location: emailContent.orderDetails?.street || '',
          city: emailContent.orderDetails?.city || '',
          zipcode: emailContent.orderDetails?.zipcode || ''
        },
        htmlBody: editableEmailBody,
        subject: editableEmailSubject,
        previewText: editablePreviewText,
        contactPersonId: selectedContactPerson
      });

      console.log('[FRONTEND] Mailgun send result:', response.data);
      setSendResult(response.data);
      setSendProgress(null);

      if (response.data.success) {
        toast.success(`Versand abgeschlossen! Erfolgreich: ${response.data.successful} von ${response.data.total}, Fehlgeschlagen: ${response.data.failed}`);
      }
    } catch (error) {
      console.error('[FRONTEND] Error sending via Mailgun:', error);
      setSendProgress(null);
      setSendResult({ success: false, error: error.response?.data?.error || error.message });
      toast.error('Fehler beim Versand: ' + (error.response?.data?.message || error.message));
    }

    setSendingEmail(false);
  };

  const loadContactPersons = async () => {
    try {
      const response = await axios.get('/api/contact-persons');
      setContactPersons(response.data.persons || []);
      if (response.data.persons && response.data.persons.length > 0 && !selectedContactPerson) {
        setSelectedContactPerson(response.data.persons[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kontaktpersonen:', error);
      if (error.response?.status === 500) {
        toast.error('Mailing-Datenbank nicht erreichbar – Kontaktpersonen konnten nicht geladen werden');
      }
    }
  };

  const loadTestRecipients = async () => {
    try {
      const response = await axios.get('/api/test-recipients/combined');
      setTestRecipients(response.data.recipients || []);
    } catch (error) {
      console.error('Fehler beim Laden der Test-Empfänger:', error);
    }
  };

  const saveContactPerson = async (contactData) => {
    try {
      if (editingContact && editingContact.id) {
        await axios.put(`/api/contact-persons/${editingContact.id}`, contactData);
      } else {
        await axios.post('/api/contact-persons', contactData);
      }
      await loadContactPersons();
      setEditingContact(null);
      toast.success('Kontaktperson erfolgreich gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern: ' + error.message);
    }
  };

  const deleteContactPerson = async (id) => {
    setConfirmDialog({
      title: 'Kontaktperson löschen',
      message: 'Möchten Sie diese Kontaktperson wirklich löschen?',
      danger: true,
      onConfirm: () => {
        setConfirmDialog(null);
        executeDeleteContactPerson(id);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const executeDeleteContactPerson = async (id) => {
    try {
      await axios.delete(`/api/contact-persons/${id}`);
      await loadContactPersons();
      toast.success('Kontaktperson gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen: ' + error.message);
    }
  };

  const loadLlmPrompt = async () => {
    setLlmPromptLoading(true);
    try {
      const response = await axios.get('/api/llm/prompt');
      if (response.data.success) {
        setLlmPrompt(response.data.prompt);
        setLlmPromptIsDefault(response.data.isDefault);
      }
    } catch (error) {
      console.error('Fehler beim Laden des LLM Prompts:', error);
      toast.error('Fehler beim Laden des Prompts: ' + (error.response?.data?.error || error.message));
    }
    setLlmPromptLoading(false);
  };

  const saveLlmPrompt = async () => {
    setLlmPromptSaving(true);
    try {
      const response = await axios.put('/api/llm/prompt', { prompt: llmPrompt });
      if (response.data.success) {
        setLlmPromptIsDefault(false);
        toast.success('Prompt erfolgreich gespeichert');
      }
    } catch (error) {
      console.error('Fehler beim Speichern des LLM Prompts:', error);
      toast.error('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    }
    setLlmPromptSaving(false);
  };

  const resetLlmPrompt = async () => {
    setLlmPromptSaving(true);
    try {
      const response = await axios.post('/api/llm/prompt/reset');
      if (response.data.success) {
        setLlmPrompt(response.data.prompt);
        setLlmPromptIsDefault(true);
        toast.success('Prompt auf Standardwert zurückgesetzt');
      }
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des LLM Prompts:', error);
      toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.error || error.message));
    }
    setLlmPromptSaving(false);
  };

  const loadSubjectPrompt = async () => {
    setSubjectPromptLoading(true);
    try {
      const response = await axios.get('/api/llm/subject-prompt');
      if (response.data.success) {
        setSubjectPrompt(response.data.prompt);
        setSubjectPromptIsDefault(response.data.isDefault);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Betreff-Prompts:', error);
      toast.error('Fehler beim Laden des Betreff-Prompts: ' + (error.response?.data?.error || error.message));
    }
    setSubjectPromptLoading(false);
  };

  const saveSubjectPrompt = async () => {
    setSubjectPromptSaving(true);
    try {
      const response = await axios.put('/api/llm/subject-prompt', { prompt: subjectPrompt });
      if (response.data.success) {
        setSubjectPromptIsDefault(false);
        toast.success('Betreff-Prompt erfolgreich gespeichert');
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Betreff-Prompts:', error);
      toast.error('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    }
    setSubjectPromptSaving(false);
  };

  const resetSubjectPrompt = async () => {
    setSubjectPromptSaving(true);
    try {
      const response = await axios.post('/api/llm/subject-prompt/reset');
      if (response.data.success) {
        setSubjectPrompt(response.data.prompt);
        setSubjectPromptIsDefault(true);
        toast.success('Betreff-Prompt auf Standardwert zurückgesetzt');
      }
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Betreff-Prompts:', error);
      toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.error || error.message));
    }
    setSubjectPromptSaving(false);
  };

  const loadPreviewPrompt = async () => {
    setPreviewPromptLoading(true);
    try {
      const response = await axios.get('/api/llm/preview-prompt');
      if (response.data.success) {
        setPreviewPrompt(response.data.prompt);
        setPreviewPromptIsDefault(response.data.isDefault);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Vorschau-Prompts:', error);
      toast.error('Fehler beim Laden des Vorschau-Prompts: ' + (error.response?.data?.error || error.message));
    }
    setPreviewPromptLoading(false);
  };

  const savePreviewPrompt = async () => {
    setPreviewPromptSaving(true);
    try {
      const response = await axios.put('/api/llm/preview-prompt', { prompt: previewPrompt });
      if (response.data.success) {
        setPreviewPromptIsDefault(false);
        toast.success('Vorschau-Prompt erfolgreich gespeichert');
      }
    } catch (error) {
      console.error('Fehler beim Speichern des Vorschau-Prompts:', error);
      toast.error('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    }
    setPreviewPromptSaving(false);
  };

  const resetPreviewPrompt = async () => {
    setPreviewPromptSaving(true);
    try {
      const response = await axios.post('/api/llm/preview-prompt/reset');
      if (response.data.success) {
        setPreviewPrompt(response.data.prompt);
        setPreviewPromptIsDefault(true);
        toast.success('Vorschau-Prompt auf Standardwert zurückgesetzt');
      }
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Vorschau-Prompts:', error);
      toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.error || error.message));
    }
    setPreviewPromptSaving(false);
  };

  const loadMailingOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/mailing/orders?days=${daysFilter}`);
      setMailingOrders(response.data.orders);
      // DB-Status wird nur beim Start geprüft
    } catch (error) {
      console.error('Fehler beim Laden der Aufträge:', error);
      toast.error('Fehler beim Laden der Aufträge: ' + error.message);
      // Bei Fehler: DB-Status neu prüfen
      await checkDatabaseStatus(true);
    }
    setLoading(false);
  };

  const loadTrackingOverview = async () => {
    setTrackingLoading(true);
    try {
      const response = await axios.get('/api/tracking/overview');
      setTrackingData(response.data.projects || []);
    } catch (error) {
      console.error('Fehler beim Laden der Tracking-Übersicht:', error);
    }
    setTrackingLoading(false);
  };

  const loadOrderHistory = async (orderId) => {
    try {
      const response = await axios.get(`/api/mailing/order-history/${orderId}`);
      setMailingHistory(response.data.campaigns || []);
      return response.data.campaigns || [];
    } catch (error) {
      console.error('[FRONTEND] Error loading order history:', error);
      setMailingHistory([]);
      return [];
    }
  };

  const selectOrder = async (orderId) => {
    console.log('[FRONTEND] selectOrder called with orderId:', orderId);
    
    // Check if this order has mailing history
    const order = mailingOrders.find(o => o.id === orderId);
    if (order && order.mailing_status && order.mailing_status.campaign_count > 0) {
      console.log('[FRONTEND] Order has mailing history, showing choice dialog');
      setLoading(true);
      try {
        const response = await axios.get(`/api/mailing/order/${orderId}`);
        setOrderDetails(response.data);
        setSelectedOrder(orderId);
        await loadOrderHistory(orderId);
        setShowMailingChoice(true);
      } catch (error) {
        console.error('[FRONTEND] Error loading order for choice:', error);
        toast.error('Fehler beim Laden des Auftrags: ' + (error.response?.data?.error || error.message));
      }
      setLoading(false);
      return;
    }

    // No mailing history — proceed directly to preparation
    await proceedToNewMailing(orderId);
  };

  const loadTradesForOrder = async (orderId) => {
    console.log('[FRONTEND] loadTradesForOrder called with orderId:', orderId);
    setTradesLoading(true);
    let orderTradeIds = [];
    try {
      const [allTradesRes, orderTradesRes] = await Promise.all([
        axios.get('/api/mailing/trades'),
        axios.get(`/api/mailing/order/${orderId}/trades`)
      ]);
      
      const allTradesData = allTradesRes.data.trades || [];
      const orderTradesData = orderTradesRes.data.trades || [];
      orderTradeIds = orderTradesData.map(t => t.id);
      
      console.log(`[FRONTEND] Loaded ${allTradesData.length} total trades, ${orderTradeIds.length} pre-selected for order`);
      setAllTrades(allTradesData);
      setSelectedTrades(orderTradeIds);
    } catch (error) {
      console.error('[FRONTEND] Error loading trades:', error);
      setAllTrades([]);
      setSelectedTrades([]);
    }
    setTradesLoading(false);
    return orderTradeIds;
  };

  const proceedToNewMailing = async (orderId) => {
    console.log('[FRONTEND] proceedToNewMailing for orderId:', orderId);
    setLoading(true);
    setSelectedOrder(orderId);
    setShowMailingChoice(false);
    setShowMailingHistory(false);
    setSelectedCampaign(null);
    try {
      console.log('[FRONTEND] Fetching order details...');
      const response = await axios.get(`/api/mailing/order/${orderId}`);
      console.log('[FRONTEND] Order details received:', response.data);
      setOrderDetails(response.data);
      
      console.log('[FRONTEND] Loading trades for order...');
      await loadTradesForOrder(orderId);
      
      setMatchingCompanies([]);
    } catch (error) {
      console.error('[FRONTEND] Error in proceedToNewMailing:', error);
      console.error('[FRONTEND] Error response:', error.response?.data);
      toast.error('Fehler beim Laden des Auftrags: ' + (error.response?.data?.error || error.message));
    }
    setLoading(false);
  };

  const startResendCampaign = async (campaign) => {
    console.log('[FRONTEND] startResendCampaign - reusing campaign:', campaign.id);
    setResendCampaignData(campaign);
    await proceedToNewMailing(selectedOrder);
  };

  const loadMatchingCompanies = async (orderId, tradesOverride) => {
    console.log('[FRONTEND] loadMatchingCompanies called with orderId:', orderId);
    setSelectedContacts({});
    setExpandedCompanies(new Set());
    setCurrentPage(1);
    
    try {
      let radiusKm = radiusFilter;
      
      if (filterType === 'traveltime') {
        radiusKm = Math.round((travelTimeFilter / 60) * 50);
        console.log(`[FRONTEND] Travel time ${travelTimeFilter} min converted to approx. ${radiusKm} km radius`);
      }
      
      const trades = tradesOverride !== undefined ? tradesOverride : selectedTrades;
      const tradesQuery = trades.length > 0 ? `&trades=${trades.join(',')}` : '';
      console.log(`[FRONTEND] Fetching matching companies with ${filterType} filter, trades: [${trades.join(',')}]...`);
      const response = await axios.get(`/api/mailing/matching-companies/${orderId}?radius=${radiusKm}${tradesQuery}`);
      console.log('[FRONTEND] Matching companies response:', response.data);
      setMatchingCompanies(response.data.companies);
      console.log(`[FRONTEND] Found ${response.data.companies.length} matching companies with contact info.`);
    } catch (error) {
      console.error('[FRONTEND] Error in loadMatchingCompanies:', error);
      console.error('[FRONTEND] Error response:', error.response?.data);
      toast.error('Fehler beim Laden der passenden Unternehmen: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleCompanySelection = (company) => {
    const companyId = company.id;
    const contacts = company.contacts || [];
    const contactIds = contacts.map(c => c.id);
    setSelectedContacts(prev => {
      const currentSelected = prev[companyId] || [];
      if (currentSelected.length === contactIds.length && contactIds.length > 0) {
        const next = { ...prev };
        delete next[companyId];
        return next;
      } else {
        return { ...prev, [companyId]: [...contactIds] };
      }
    });
  };

  const toggleContactSelection = (companyId, contactId) => {
    setSelectedContacts(prev => {
      const currentSelected = prev[companyId] || [];
      const isSelected = currentSelected.includes(contactId);
      let newSelected;
      if (isSelected) {
        newSelected = currentSelected.filter(id => id !== contactId);
      } else {
        newSelected = [...currentSelected, contactId];
      }
      const next = { ...prev };
      if (newSelected.length === 0) {
        delete next[companyId];
      } else {
        next[companyId] = newSelected;
      }
      return next;
    });
  };

  const toggleExpandCompany = (companyId) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const selectAllCompanies = (companies) => {
    const newSelected = {};
    companies.forEach(company => {
      const contactIds = (company.contacts || []).map(c => c.id);
      if (contactIds.length > 0) {
        newSelected[company.id] = contactIds;
      }
    });
    setSelectedContacts(newSelected);
  };

  const deselectAllCompanies = () => {
    setSelectedContacts({});
  };

  const getSelectedCompanyCount = () => {
    return Object.keys(selectedContacts).filter(k => selectedContacts[k]?.length > 0).length;
  };

  const getTotalSelectedContactCount = () => {
    return Object.values(selectedContacts).reduce((sum, ids) => sum + ids.length, 0);
  };

  const generateEmail = async () => {
    if (emailMode === 'production' && getTotalSelectedContactCount() === 0) {
      toast.error('Bitte wählen Sie mindestens einen Kontakt aus');
      return;
    }
    
    if (emailMode === 'test' && selectedTestRecipients.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Test-Empfänger aus');
      return;
    }

    setLoading(true);
    setEmailGenerationProgress(emailMode === 'test' ? 'Bereite Test-Empfänger vor...' : 'Sammle E-Mail-Adressen...');
    
    try {
      let recipients = [];
      
      if (emailMode === 'production') {
        console.log('[FRONTEND] Collecting contacts from selected contacts...');
        Object.entries(selectedContacts).forEach(([companyId, contactIds]) => {
          const company = matchingCompanies.find(c => String(c.id) === String(companyId));
          if (!company) return;
          contactIds.forEach(contactId => {
            const contact = (company.contacts || []).find(ct => ct.id === contactId);
            if (contact && contact.email) {
              recipients.push({
                email: contact.email,
                name: `${contact.firstname || ''} ${contact.lastname || ''}`.trim(),
                company: company.name
              });
            }
          });
        });

        if (recipients.length === 0) {
          toast.error('Keine E-Mail-Adressen für die ausgewählten Unternehmen gefunden');
          setLoading(false);
          setEmailGenerationProgress(null);
          return;
        }
      } else {
        console.log('[FRONTEND] Preparing test recipients...');
        selectedTestRecipients.forEach(recipientId => {
          const recipient = testRecipients.find(r => r.sourceId === recipientId);
          if (recipient) {
            recipients.push({
              email: recipient.email,
              name: `${recipient.firstname} ${recipient.lastname}`,
              company: recipient.company || 'Test'
            });
          }
        });
      }

      // If resending an existing campaign, skip AI generation
      if (resendCampaignData) {
        console.log('[FRONTEND] Resending existing campaign, skipping AI generation');
        setEmailContent({ 
          subject: resendCampaignData.subject, 
          body: resendCampaignData.html_body,
          previewText: '',
          recipients: recipients
        });
        setEditableEmailBody(resendCampaignData.html_body);
        setEditableEmailSubject(resendCampaignData.subject);
        setEditablePreviewText('');
        setShowEmailPreview(true);
        setEmailGenerationProgress(null);
        setLoading(false);
        return;
      }

      console.log(`[FRONTEND] Generating email for ${recipients.length} recipients...`);
      setEmailGenerationProgress(`E-Mail wird mit KI generiert für ${recipients.length} Empfänger...`);
      const response = await axios.post('/api/mailing/generate-email', {
        orderId: selectedOrder,
        recipients: recipients,
        contactPersonId: selectedContactPerson
      });
      const emailData = response.data.emailContent;
      
      let emailBodyWithSignature = emailData.body;
      if (selectedContactPerson) {
        const contactPerson = contactPersons.find(p => p.id === selectedContactPerson);
        if (contactPerson) {
          const signature = `
<br><br>
<p>Bei Fragen zu diesem Auftrag oder Handwerk Connected stehe ich dir gerne zur Verfügung!</p>
<p><strong>Beste Grüße</strong><br>
${contactPerson.firstname} ${contactPerson.lastname}<br>
${contactPerson.position || ''}<br>
${contactPerson.phone ? `Tel.: ${contactPerson.phone}<br>` : ''}
<a href="mailto:${contactPerson.email}">${contactPerson.email}</a><br>
<a href="https://${contactPerson.website}">${contactPerson.website}</a></p>`;
          emailBodyWithSignature = emailData.body + signature;
        }
      }
      
      setEmailContent(emailData);
      setEditableEmailBody(emailBodyWithSignature);
      setEditableEmailSubject(emailData.subject);
      setEditablePreviewText(emailData.previewText || '');
      setShowEmailPreview(true);
      setEmailGenerationProgress(null);
    } catch (error) {
      console.error('[FRONTEND] Error generating email:', error);
      toast.error('Fehler beim Generieren der E-Mail: ' + (error.response?.data?.error || error.message));
      setEmailGenerationProgress(null);
    }
    
    setLoading(false);
  };

  const renderDashboard = () => {
    const s = dashboardStats;
    const fmt = (n) => (n ?? 0).toLocaleString('de-DE');

    return (
      <div className="dashboard">
        <div className="dash-header">
          <h2>Übersicht</h2>
          <button className="btn btn-secondary btn-small" onClick={loadDashboardStats} disabled={dashboardLoading}>
            <RefreshCw size={16} className={dashboardLoading ? 'spin' : ''} />
            Aktualisieren
          </button>
        </div>

        {dashboardLoading && !s && (
          <div className="dash-loading">Lade Statistiken…</div>
        )}

        {s && (
          <>
            {/* KPI Cards Row 1 */}
            <div className="dash-kpi-grid">
              <div className="dash-kpi-card dash-kpi--blue">
                <div className="dash-kpi-icon"><Briefcase size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.orders.total)}</span>
                  <span className="dash-kpi-label">Aufträge (90 Tage)</span>
                </div>
              </div>

              <div className="dash-kpi-card dash-kpi--teal">
                <div className="dash-kpi-icon"><Send size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.orders.mailed)}</span>
                  <span className="dash-kpi-label">Per Mailing angeschrieben</span>
                </div>
              </div>

              <div className="dash-kpi-card dash-kpi--green">
                <div className="dash-kpi-icon"><MousePointerClick size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.tracking.unique_clickers)}</span>
                  <span className="dash-kpi-label">Reagiert (Link-Klick)</span>
                </div>
              </div>

              <div className="dash-kpi-card dash-kpi--amber">
                <div className="dash-kpi-icon"><TrendingUp size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{s.tracking.click_rate}%</span>
                  <span className="dash-kpi-label">Klickrate</span>
                </div>
              </div>
            </div>

            {/* KPI Cards Row 2 */}
            <div className="dash-kpi-grid">
              <div className="dash-kpi-card dash-kpi--indigo">
                <div className="dash-kpi-icon"><Mail size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.campaigns.total_successful)}</span>
                  <span className="dash-kpi-label">E-Mails erfolgreich versendet</span>
                </div>
              </div>

              <div className="dash-kpi-card dash-kpi--purple">
                <div className="dash-kpi-icon"><BarChart2 size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.campaigns.production)}</span>
                  <span className="dash-kpi-label">Produktions-Kampagnen</span>
                </div>
              </div>

              <div className="dash-kpi-card dash-kpi--rose">
                <div className="dash-kpi-icon"><Eye size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.tracking.total_clicks)}</span>
                  <span className="dash-kpi-label">Gesamt-Klicks</span>
                </div>
              </div>

              <div className="dash-kpi-card dash-kpi--slate">
                <div className="dash-kpi-icon"><Users size={28} /></div>
                <div className="dash-kpi-body">
                  <span className="dash-kpi-value">{fmt(s.contact_persons)}</span>
                  <span className="dash-kpi-label">Kontaktpersonen</span>
                </div>
              </div>
            </div>

            {/* Lead Funnel */}
            {(s.leads.total_leads > 0) && (
              <div className="dash-section">
                <h3><Target size={20} /> Lead-Funnel</h3>
                <div className="dash-funnel">
                  <div className="dash-funnel-step">
                    <div className="dash-funnel-bar" style={{ width: '100%', background: '#e0e7ff' }}></div>
                    <div className="dash-funnel-info">
                      <span className="dash-funnel-label">Pool</span>
                      <span className="dash-funnel-count">{fmt(s.leads.pool)}</span>
                    </div>
                  </div>
                  <div className="dash-funnel-step">
                    <div className="dash-funnel-bar" style={{ 
                      width: s.leads.total_leads > 0 ? `${Math.max(((s.leads.mailed / s.leads.total_leads) * 100), 5)}%` : '5%', 
                      background: '#bfdbfe' 
                    }}></div>
                    <div className="dash-funnel-info">
                      <span className="dash-funnel-label">Angeschrieben</span>
                      <span className="dash-funnel-count">{fmt(s.leads.mailed)}</span>
                    </div>
                  </div>
                  <div className="dash-funnel-step">
                    <div className="dash-funnel-bar" style={{ 
                      width: s.leads.total_leads > 0 ? `${Math.max(((s.leads.interested / s.leads.total_leads) * 100), 5)}%` : '5%', 
                      background: '#bbf7d0' 
                    }}></div>
                    <div className="dash-funnel-info">
                      <span className="dash-funnel-label">Interessiert</span>
                      <span className="dash-funnel-count">{fmt(s.leads.interested)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bottom: Recent Activity */}
            <div className="dash-bottom-grid">
              {/* Recent Campaigns */}
              <div className="dash-section">
                <h3><Clock size={20} /> Letzte Kampagnen</h3>
                {s.recent_campaigns.length === 0 ? (
                  <p className="dash-empty">Noch keine Kampagnen versendet.</p>
                ) : (
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>Betreff</th>
                          <th>Typ</th>
                          <th>Empf.</th>
                          <th>OK</th>
                          <th>Datum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.recent_campaigns.map(c => (
                          <tr key={c.id}>
                            <td className="dash-td-subject" title={c.subject}>{c.subject}</td>
                            <td>
                              <span className={`dash-badge ${c.campaign_type === 'production' ? 'dash-badge--prod' : 'dash-badge--test'}`}>
                                {c.campaign_type === 'production' ? 'Prod' : 'Test'}
                              </span>
                            </td>
                            <td>{c.recipient_count}</td>
                            <td>{c.successful}</td>
                            <td className="dash-td-date">{new Date(c.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Recent Clicks */}
              <div className="dash-section">
                <h3><MousePointerClick size={20} /> Letzte Link-Klicks</h3>
                {s.recent_clicks.length === 0 ? (
                  <p className="dash-empty">Noch keine Klicks registriert.</p>
                ) : (
                  <div className="dash-table-wrap">
                    <table className="dash-table">
                      <thead>
                        <tr>
                          <th>E-Mail</th>
                          <th>Projekt</th>
                          <th>Klicks</th>
                          <th>Datum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.recent_clicks.map((click, i) => (
                          <tr key={i}>
                            <td className="dash-td-email" title={click.recipient_email}>{click.recipient_email}</td>
                            <td>#{click.project_id}</td>
                            <td>{click.click_count}</td>
                            <td className="dash-td-date">{click.clicked_at ? new Date(click.clicked_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Failure summary */}
            {s.campaigns.total_failed > 0 && (
              <div className="dash-alert dash-alert--warn">
                <XCircle size={18} />
                <span>{fmt(s.campaigns.total_failed)} E-Mails sind beim Versand fehlgeschlagen (gesamt über alle Kampagnen).</span>
              </div>
            )}
          </>
        )}

        {!s && !dashboardLoading && (
          <div className="dash-empty-state">
            <Activity size={48} />
            <p>Dashboard-Daten konnten nicht geladen werden.</p>
            <button className="btn btn-primary" onClick={loadDashboardStats}>Erneut versuchen</button>
          </div>
        )}
      </div>
    );
  };

  const renderRelationships = () => (
    <div className="relationships-view">
      <div className="view-header">
        <h2>Datenbank Beziehungen</h2>
        <button className="btn btn-secondary" onClick={loadRelationships} disabled={loading}>
          <RefreshCw size={18} />
          Aktualisieren
        </button>
      </div>

      {Object.keys(relationships).length > 0 ? (
        <div className="relationships-container">
          {Object.entries(relationships).map(([tableName, relations]) => (
            <div key={tableName} className="relationship-card">
              <div className="relationship-header">
                <GitBranch size={20} />
                <h3>{tableName}</h3>
                <span className="relation-count">{relations.length} Beziehungen</span>
              </div>
              <div className="relationship-list">
                {relations.map((rel, idx) => (
                  <div key={idx} className={`relationship-item ${rel.type}`}>
                    {rel.type === 'belongsTo' ? (
                      <>
                        <Key size={16} />
                        <span className="rel-type">belongsTo</span>
                        <span className="rel-detail">
                          {rel.foreignKey} <ArrowRight size={14} /> {rel.relatedTable}.{rel.relatedColumn}
                        </span>
                      </>
                    ) : (
                      <>
                        <Database size={16} />
                        <span className="rel-type">hasMany</span>
                        <span className="rel-detail">
                          {rel.throughTable} (via {rel.throughColumn})
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-data">Keine Beziehungen gefunden oder noch nicht geladen.</p>
      )}
    </div>
  );

  const renderEmailMailing = () => {
    // Email Preview Page
    if (showEmailPreview && emailContent) {
      return (
        <div className="mailing-view">
          <div className="view-header">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowEmailPreview(false)}
              style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
            >
              <ArrowLeft size={18} />
              Zurück zur Auswahl
            </button>
            <h2>E-Mail Vorschau</h2>
          </div>

          <div className="email-preview-section">
            <div className="email-preview">
              <div className="email-field">
                <strong>Betreff:</strong>
                <input 
                  type="text" 
                  value={editableEmailSubject}
                  onChange={(e) => setEditableEmailSubject(e.target.value)}
                  className="email-subject-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                />
              </div>
              <div className="email-field">
                <strong>Vorschautext (Preheader):</strong>
                <input 
                  type="text" 
                  value={editablePreviewText}
                  onChange={(e) => setEditablePreviewText(e.target.value)}
                  placeholder="Kurzer Text, der in E-Mail-Clients neben dem Betreff angezeigt wird..."
                  className="email-subject-input"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#4b5563'
                  }}
                />
                <p style={{fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem'}}>
                  Wird in E-Mail-Clients als Vorschau neben der Betreffzeile angezeigt
                </p>
              </div>
              <div className="email-field">
                <strong>Empfänger ({emailContent.recipients.length}):</strong>
                <div className="recipients-list">
                  {emailContent.recipients.map((recipient, idx) => (
                    <span key={idx} className="recipient-badge">
                      {recipient.name} ({recipient.email})
                    </span>
                  ))}
                </div>
              </div>
              <div className="email-field">
                <strong>Nachricht:</strong>
                <div 
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => setEditableEmailBody(e.currentTarget.innerHTML)}
                  dangerouslySetInnerHTML={{ __html: editableEmailBody }}
                  className="email-body-editor"
                  style={{
                    background: '#f9fafb',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    padding: '1.5rem',
                    minHeight: '300px',
                    fontSize: '0.9375rem',
                    lineHeight: '1.8',
                    color: '#1f2937',
                    overflowY: 'auto',
                    cursor: 'text'
                  }}
                />
                <p style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem'}}>
                  ℹ️ Sie können den Text direkt bearbeiten
                </p>
              </div>
              {emailContent.personalizedNote && (
                <div className="email-field" style={{
                  background: '#fff8dd',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #edb600'
                }}>
                  <strong>ℹ️ {emailContent.personalizedNote}</strong>
                </div>
              )}
              <div className="email-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={emailMode === 'test' ? sendTestEmail : sendProductionEmail}
                  disabled={sendingEmail}
                  style={{display: 'flex', alignItems: 'center', gap: '0.5rem', background: emailMode === 'test' ? '#0ea5e9' : '#dc2626'}}
                >
                  {sendingEmail ? (
                    <><RefreshCw size={18} className="spin" /> Wird gesendet...</>
                  ) : emailMode === 'test' ? (
                    <><Mail size={18} /> 🧪 Test-E-Mail senden ({selectedTestRecipients.length} Empfänger)</>
                  ) : (
                    <><Mail size={18} /> ⚠️ PRODUKTIONS-VERSAND ({emailContent.recipients.length} Empfänger)</>
                  )}
                </button>
                <button className="btn btn-secondary" onClick={() => {
                  navigator.clipboard.writeText(editableEmailBody);
                  toast.success('E-Mail-HTML in die Zwischenablage kopiert');
                }}>
                  HTML kopieren
                </button>
              </div>

              {false && showSendDialog && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    background: 'white',
                    borderRadius: '0.75rem',
                    padding: '2rem',
                    maxWidth: '600px',
                    width: '90%',
                    maxHeight: '80vh',
                    overflowY: 'auto'
                  }}>
                    <h3 style={{marginTop: 0}}>E-Mail versenden</h3>
                    <p style={{color: '#6b7280'}}>Wählen Sie, ob Sie die E-Mail testen oder an echte Empfänger senden möchten.</p>
                    
                    <div style={{marginTop: '1.5rem'}}>
                      <h4>Test-Versand</h4>
                      <p style={{fontSize: '0.875rem', color: '#6b7280'}}>Wählen Sie Test-Empfänger aus (Kontaktpersonen und Test-Empfänger):</p>
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        padding: '0.75rem',
                        marginBottom: '1rem'
                      }}>
                        {testRecipients.map((recipient) => (
                          <label key={recipient.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            borderRadius: '0.25rem',
                            marginBottom: '0.25rem',
                            background: selectedTestRecipients.includes(recipient.sourceId) ? '#e0f2fe' : 'transparent'
                          }}>
                            <input 
                              type="checkbox"
                              checked={selectedTestRecipients.includes(recipient.sourceId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTestRecipients([...selectedTestRecipients, recipient.sourceId]);
                                } else {
                                  setSelectedTestRecipients(selectedTestRecipients.filter(id => id !== recipient.sourceId));
                                }
                              }}
                              style={{marginRight: '0.75rem'}}
                            />
                            <div>
                              <div style={{fontWeight: '500'}}>{recipient.firstname} {recipient.lastname}</div>
                              <div style={{fontSize: '0.875rem', color: '#6b7280'}}>
                                {recipient.email} {recipient.source === 'contact' && '(Kontaktperson)'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                      <button 
                        className="btn btn-secondary"
                        onClick={sendTestEmail}
                        disabled={selectedTestRecipients.length === 0}
                        style={{width: '100%', marginBottom: '1rem'}}
                      >
                        Test-E-Mail senden ({selectedTestRecipients.length} ausgewählt)
                      </button>
                    </div>

                    <div style={{
                      borderTop: '2px solid #e5e7eb',
                      marginTop: '1.5rem',
                      paddingTop: '1.5rem'
                    }}>
                      <h4 style={{color: '#dc2626'}}>⚠️ Produktions-Versand</h4>
                      <p style={{fontSize: '0.875rem', color: '#6b7280'}}>
                        Sendet die E-Mail an <strong>{emailContent.recipients.length} echte Empfänger</strong>. Dieser Vorgang kann nicht rückgängig gemacht werden!
                      </p>
                      <button 
                        className="btn btn-primary"
                        onClick={sendProductionEmail}
                        style={{width: '100%', background: '#dc2626', marginBottom: '1rem'}}
                      >
                        An {emailContent.recipients.length} Empfänger senden
                      </button>
                    </div>

                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowSendDialog(false);
                        setSelectedTestRecipients([]);
                      }}
                      style={{width: '100%'}}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}

              {sendProgress && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#e0f2fe',
                  border: '1px solid #0ea5e9',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <RefreshCw size={18} className="spin" />
                  <span>{sendProgress}</span>
                </div>
              )}

              {sendResult && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: sendResult.success ? '#dcfce7' : '#fee2e2',
                  border: `1px solid ${sendResult.success ? '#22c55e' : '#ef4444'}`,
                  borderRadius: '0.5rem'
                }}>
                  {sendResult.success ? (
                    <div>
                      <p style={{margin: '0 0 0.5rem 0', fontWeight: '600', color: '#16a34a'}}>
                        ✅ Versand abgeschlossen
                      </p>
                      <p style={{margin: 0, fontSize: '0.875rem'}}>
                        <strong>{sendResult.successful}</strong> von <strong>{sendResult.total}</strong> Kontakte erfolgreich an Mailgun übergeben.
                        {sendResult.failed > 0 && (
                          <span style={{color: '#dc2626'}}> {sendResult.failed} fehlgeschlagen.</span>
                        )}
                      </p>
                      <p style={{margin: '0.5rem 0 0 0', fontSize: '0.8125rem', color: '#6b7280'}}>
                        Die E-Mails wurden über Mailgun versendet.
                      </p>
                      <button
                        className="btn btn-primary"
                        style={{marginTop: '1rem'}}
                        onClick={() => {
                          setSelectedOrder(null);
                          setOrderDetails(null);
                          setMatchingCompanies([]);
                          setSelectedContacts({});
                          setExpandedCompanies(new Set());
                          setEmailContent(null);
                          setShowMailingChoice(false);
                          setShowMailingHistory(false);
                          setSelectedCampaign(null);
                          setMailingHistory([]);
                          setResendCampaignData(null);
                          setSendResult(null);
                          setSendProgress(null);
                          setShowEmailPreview(false);
                        }}
                      >
                        <ArrowRight size={18} style={{ transform: 'rotate(180deg)' }} />
                        Zurück zur Übersicht
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p style={{margin: 0, fontWeight: '600', color: '#dc2626'}}>
                        ❌ Versand fehlgeschlagen: {sendResult.error}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Mailing History Detail View
    if (showMailingHistory && selectedCampaign) {
      return (
        <div className="mailing-view">
          <div className="view-header">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowMailingHistory(false);
                setSelectedCampaign(null);
                setShowMailingChoice(true);
              }}
              style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
            >
              <ArrowLeft size={18} />
              Zurück zur Übersicht
            </button>
            <h2>Mailing-Details</h2>
          </div>

          <div style={{marginTop: '1.5rem'}}>
            <div className="details-card" style={{marginBottom: '1.5rem'}}>
              <div className="details-header" style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                <Mail size={24} />
                <div>
                  <h4 style={{margin: 0}}>{selectedCampaign.subject || 'Kein Betreff'}</h4>
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280'}}>
                    Versendet am {new Date(selectedCampaign.created_at).toLocaleString('de-DE')}
                    {' · '}{selectedCampaign.campaign_type === 'test' ? '🧪 Test' : '📤 Produktion'}
                    {' · '}{selectedCampaign.successful || 0} erfolgreich, {selectedCampaign.failed || 0} fehlgeschlagen
                  </p>
                </div>
              </div>
            </div>

            <div style={{marginBottom: '1.5rem'}}>
              <h3 style={{marginBottom: '0.75rem'}}>
                <Users size={18} style={{verticalAlign: 'middle', marginRight: '0.5rem'}} />
                Empfänger ({selectedCampaign.recipients?.length || 0})
              </h3>
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                overflow: 'hidden'
              }}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem'}}>
                  <thead>
                    <tr style={{background: '#f9fafb'}}>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Name</th>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>E-Mail</th>
                      <th style={{padding: '0.75rem 1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Unternehmen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedCampaign.recipients || []).map((r, idx) => (
                      <tr key={idx} style={{borderBottom: '1px solid #f3f4f6'}}>
                        <td style={{padding: '0.5rem 1rem'}}>{r.name || '-'}</td>
                        <td style={{padding: '0.5rem 1rem'}}>{r.email || '-'}</td>
                        <td style={{padding: '0.5rem 1rem'}}>{r.company || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 style={{marginBottom: '0.75rem'}}>
                <Mail size={18} style={{verticalAlign: 'middle', marginRight: '0.5rem'}} />
                E-Mail-Text
              </h3>
              <div style={{
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                fontSize: '0.9375rem',
                lineHeight: '1.8',
                color: '#1f2937'
              }}
                dangerouslySetInnerHTML={{ __html: selectedCampaign.html_body || '<em>Kein Inhalt verfügbar</em>' }}
              />
            </div>

            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: '#f0f9ff',
              border: '2px solid #006691',
              borderRadius: '0.75rem',
              textAlign: 'center'
            }}>
              <p style={{margin: '0 0 1rem 0', fontSize: '0.9375rem', color: '#1f2937'}}>
                Gleiches Mailing an weitere Empfänger senden?
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => startResendCampaign(selectedCampaign)}
                style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}
              >
                <Users size={18} />
                An weitere Empfänger senden
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Choice Dialog: View history or create new mailing
    if (showMailingChoice && selectedOrder) {
      const orderTitle = orderDetails?.order?.title || 'Auftrag';
      return (
        <div className="mailing-view">
          <div className="view-header">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowMailingChoice(false);
                setSelectedOrder(null);
                setOrderDetails(null);
                setMailingHistory([]);
              }}
              style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
            >
              <ArrowLeft size={18} />
              Zurück zur Auswahl
            </button>
            <h2>{orderTitle}</h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
            marginTop: '1.5rem'
          }}>
            <div 
              onClick={() => proceedToNewMailing(selectedOrder)}
              style={{
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '0.75rem',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#006691'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,102,145,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Mail size={28} color="#006691" />
              </div>
              <h3 style={{margin: '0 0 0.5rem 0', fontSize: '1.125rem'}}>Neues Mailing erstellen</h3>
              <p style={{margin: 0, fontSize: '0.875rem', color: '#6b7280'}}>
                Weitere Empfänger auswählen und ein neues Mailing versenden
              </p>
            </div>

            <div 
              onClick={() => {
                setShowMailingChoice(false);
                setShowMailingHistory(true);
              }}
              style={{
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '0.75rem',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(34,197,94,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Clock size={28} color="#16a34a" />
              </div>
              <h3 style={{margin: '0 0 0.5rem 0', fontSize: '1.125rem'}}>Bisherige Mailings ansehen</h3>
              <p style={{margin: 0, fontSize: '0.875rem', color: '#6b7280'}}>
                Empfängerlisten und E-Mail-Texte vergangener Mailings einsehen
              </p>
            </div>
          </div>

          {mailingHistory.length > 0 && (
            <div style={{marginTop: '2rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.75rem', border: '1px solid #e5e7eb'}}>
              <p style={{margin: 0, fontSize: '0.875rem', color: '#6b7280'}}>
                <strong>{mailingHistory.filter(c => c.campaign_type === 'production').length}</strong> Produktions-Mailing(s) und{' '}
                <strong>{mailingHistory.filter(c => c.campaign_type === 'test').length}</strong> Test-Mailing(s) vorhanden
              </p>
            </div>
          )}
        </div>
      );
    }

    // Mailing History List View
    if (showMailingHistory && selectedOrder && !selectedCampaign) {
      return (
        <div className="mailing-view">
          <div className="view-header">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowMailingHistory(false);
                setShowMailingChoice(true);
              }}
              style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
            >
              <ArrowLeft size={18} />
              Zurück
            </button>
            <h2>Mailing-Historie</h2>
          </div>

          {mailingHistory.length > 0 ? (
            <div style={{marginTop: '1.5rem'}}>
              <div className="orders-grid">
                {mailingHistory.map((campaign) => (
                  <div 
                    key={campaign.id} 
                    className="order-card"
                    onClick={() => setSelectedCampaign(campaign)}
                  >
                    <div className="order-card-header">
                      <Mail size={20} />
                      <h4 style={{fontSize: '0.95rem'}}>{campaign.subject || 'Kein Betreff'}</h4>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      background: campaign.campaign_type === 'production' ? '#dcfce7' : '#e0f2fe',
                      border: `1px solid ${campaign.campaign_type === 'production' ? '#22c55e' : '#0ea5e9'}`,
                      borderRadius: '0.5rem',
                      fontSize: '0.8rem',
                      color: campaign.campaign_type === 'production' ? '#15803d' : '#0369a1',
                      marginTop: '0.25rem'
                    }}>
                      {campaign.campaign_type === 'production' ? <CheckCircle2 size={14} /> : <User size={14} />}
                      <span>{campaign.campaign_type === 'production' ? 'Produktion' : 'Test'}</span>
                    </div>
                    <div className="order-card-content">
                      <div className="order-meta">
                        <div className="meta-item">
                          <Users size={14} />
                          <span>{campaign.recipient_count} Empfänger</span>
                        </div>
                        <div className="meta-item">
                          <CheckCircle size={14} color="#22c55e" />
                          <span>{campaign.successful || 0} OK</span>
                          {campaign.failed > 0 && (
                            <span style={{color: '#dc2626', marginLeft: '0.5rem'}}>
                              <XCircle size={14} style={{verticalAlign: 'middle'}} /> {campaign.failed} Fehler
                            </span>
                          )}
                        </div>
                        <div className="meta-item">
                          <Clock size={14} />
                          <span>{new Date(campaign.created_at).toLocaleString('de-DE')}</span>
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-small btn-secondary">
                      Details ansehen <ArrowRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-data">Keine Mailings vorhanden.</p>
          )}
        </div>
      );
    }

    if (!selectedOrder) {
      return (
        <div className="mailing-view">
          <div className="view-header">
            <h2>E-Mailing für neue Aufträge</h2>
            <div className="header-actions">
              <label className="filter-label">
                Zeitraum:
                <select 
                  value={daysFilter} 
                  onChange={(e) => setDaysFilter(Number(e.target.value))}
                  className="filter-select"
                >
                  <option value={7}>Letzte 7 Tage</option>
                  <option value={14}>Letzte 14 Tage</option>
                  <option value={30}>Letzte 30 Tage</option>
                  <option value={60}>Letzte 60 Tage</option>
                </select>
              </label>
              <button className="btn btn-primary" onClick={loadMailingOrders} disabled={loading}>
                <RefreshCw size={18} />
                {loading ? 'Laden...' : 'Aufträge laden'}
              </button>
            </div>
          </div>

          {mailingOrders.length > 0 ? (
            <div className="orders-list">
              <h3>Veröffentlichte Aufträge ({mailingOrders.length})</h3>
              <div className="orders-grid">
                {mailingOrders.map((order) => (
                  <div key={order.id} className="order-card" onClick={() => selectOrder(order.id)}>
                    <div className="order-card-header">
                      <Briefcase size={20} />
                      <h4>{order.title || 'Ohne Titel'}</h4>
                    </div>
                    {order.mailing_status && order.mailing_status.campaign_count > 0 && (() => {
                      const details = order.mailing_status.campaigns_detail || [];
                      const prodCount = order.mailing_status.production_count || 0;
                      const testCount = order.mailing_status.test_count || 0;
                      const allRecipients = [];
                      const seenEmails = new Set();
                      details.forEach(d => (d.recipients || []).forEach(r => {
                        if (r.email && !seenEmails.has(r.email)) {
                          seenEmails.add(r.email);
                          allRecipients.push(r);
                        }
                      }));
                      const hasProduction = prodCount > 0;
                      return (
                        <div style={{
                          padding: '0.6rem 0.75rem',
                          background: hasProduction ? '#dcfce7' : '#e0f2fe',
                          border: `1px solid ${hasProduction ? '#22c55e' : '#0ea5e9'}`,
                          borderRadius: '0.5rem',
                          fontSize: '0.8rem',
                          color: hasProduction ? '#15803d' : '#0369a1',
                          marginTop: '0.25rem'
                        }}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: allRecipients.length > 0 ? '0.4rem' : 0}}>
                            <CheckCircle2 size={14} />
                            <span>
                              {prodCount > 0 && <><strong>{prodCount}x</strong> Produktion</>}
                              {prodCount > 0 && testCount > 0 && ' · '}
                              {testCount > 0 && <><strong>{testCount}x</strong> Test</>}
                              {' · '}{order.mailing_status.total_successful || 0} Empfänger
                              {order.mailing_status.last_sent_at && (
                                <> · {new Date(order.mailing_status.last_sent_at).toLocaleDateString('de-DE')}</>
                              )}
                            </span>
                          </div>
                          {details.length > 0 && details[0].subject && (
                            <div style={{fontSize: '0.75rem', color: '#374151', marginBottom: '0.25rem'}}>
                              <Mail size={11} style={{verticalAlign: 'middle', marginRight: '0.25rem'}} />
                              <strong>Betreff:</strong> {details[0].subject}
                            </div>
                          )}
                          {allRecipients.length > 0 && (
                            <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              <Users size={11} style={{verticalAlign: 'middle', marginRight: '0.25rem'}} />
                              <strong>An:</strong>{' '}
                              {allRecipients.slice(0, 3).map(r => r.name || r.email).join(', ')}
                              {allRecipients.length > 3 && ` und ${allRecipients.length - 3} weitere`}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div className="order-card-content">
                      <p className="order-description">
                        {order.description ? (order.description.substring(0, 150) + '...') : 'Keine Beschreibung'}
                      </p>
                      <div className="order-meta">
                        <div className="meta-item">
                          <MapPin size={14} />
                          <span>{order.city || 'Kein Ort'}</span>
                        </div>
                        <div className="meta-item">
                          <span className="order-date">
                            {order.date ? new Date(order.date).toLocaleDateString('de-DE') : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-small btn-primary">
                      {order.mailing_status && order.mailing_status.campaign_count > 0 ? 'Öffnen' : 'Auswählen'} <ArrowRight size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-data">
              {loading ? 'Laden...' : 'Keine veröffentlichten Aufträge gefunden. Klicken Sie auf "Aufträge laden".'}
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="mailing-preparation-view">
        <div className="view-header">
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setSelectedOrder(null);
              setOrderDetails(null);
              setMatchingCompanies([]);
              setSelectedContacts({});
              setExpandedCompanies(new Set());
              setEmailContent(null);
              setShowMailingChoice(false);
              setShowMailingHistory(false);
              setSelectedCampaign(null);
              setMailingHistory([]);
              setResendCampaignData(null);
            }}
          >
            <ArrowRight size={18} style={{ transform: 'rotate(180deg)' }} />
            Zurück zur Auswahl
          </button>
          <h2>E-Mail Vorbereitung</h2>
        </div>

        {orderDetails && (
          <>
            <div className="order-details-section">
              <h3>Auftragsdetails</h3>
              <div className="details-card">
                <div className="details-header">
                  <Briefcase size={24} />
                  <div>
                    <h4>{orderDetails.order.title || 'Ohne Titel'}</h4>
                    <p className="client-info">
                      <strong>Auftraggeber:</strong> {orderDetails.order.company_name || 'Nicht angegeben'}
                    </p>
                  </div>
                </div>
                <div className="details-content">
                  <div className="detail-row">
                    <strong>Beschreibung:</strong>
                    <p>{orderDetails.order.description || 'Keine Beschreibung'}</p>
                  </div>
                  <div className="detail-row">
                    <strong>Ort:</strong>
                    <span>{orderDetails.order.city || 'Nicht angegeben'} {orderDetails.order.zipcode ? `(${orderDetails.order.zipcode})` : ''}</span>
                  </div>
                  <div className="detail-row">
                    <strong>Straße:</strong>
                    <span>{orderDetails.order.street || 'Nicht angegeben'}</span>
                  </div>
                </div>
              </div>
            </div>

            {resendCampaignData && (
              <div style={{
                marginTop: '1rem',
                marginBottom: '1rem',
                padding: '1rem',
                background: '#f0f9ff',
                border: '2px solid #006691',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <Mail size={20} color="#006691" />
                <div style={{flex: 1}}>
                  <strong style={{color: '#006691'}}>Bestehendes Mailing wird wiederverwendet</strong>
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280'}}>
                    Betreff: &quot;{resendCampaignData.subject}&quot; — Wählen Sie weitere Empfänger aus. Die E-Mail wird ohne KI-Generierung mit dem bestehenden Inhalt versendet.
                  </p>
                </div>
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => setResendCampaignData(null)}
                  style={{whiteSpace: 'nowrap'}}
                >
                  Abbrechen
                </button>
              </div>
            )}

            <div className="matching-companies-section">
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <h3 style={{marginBottom: 0}}>
                  {emailMode === 'production' ? 'Passende Unternehmen' : 'Kontaktpersonen (Test-Modus)'}
                </h3>
              </div>

              <div className="filter-controls" style={{flexDirection: 'column', gap: '1rem', alignItems: 'flex-start', marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
                <div style={{display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'}}>
                  <label className="filter-label" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <input 
                      type="radio" 
                      name="filterType"
                      value="radius"
                      checked={filterType === 'radius'}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    <Filter size={18} />
                    Umkreis (km):
                    <input 
                      type="number" 
                      value={radiusFilter} 
                      onChange={(e) => setRadiusFilter(Number(e.target.value))}
                      min="1"
                      max="500"
                      className="filter-input-number"
                      disabled={filterType !== 'radius'}
                    />
                  </label>

                  <label className="filter-label" style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <input 
                      type="radio" 
                      name="filterType"
                      value="traveltime"
                      checked={filterType === 'traveltime'}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    <Target size={18} />
                    Fahrtzeit (Minuten):
                    <select 
                      value={travelTimeFilter} 
                      onChange={(e) => setTravelTimeFilter(Number(e.target.value))}
                      className="filter-select"
                      disabled={filterType !== 'traveltime'}
                    >
                      <option value={30}>30 Min</option>
                      <option value={60}>1 Stunde</option>
                      <option value={90}>1,5 Stunden</option>
                      <option value={120}>2 Stunden</option>
                    </select>
                  </label>
                </div>

                <button 
                  className="btn btn-primary" 
                  onClick={() => loadMatchingCompanies(selectedOrder)}
                  disabled={loading}
                >
                  <Target size={18} />
                  Filter anwenden
                </button>

                {allTrades.length > 0 && (
                  <div style={{width: '100%', marginTop: '0.5rem'}}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem'}}>
                      <strong style={{fontSize: '0.875rem'}}>Gewerke-Filter ({selectedTrades.length} von {allTrades.length} ausgewählt):</strong>
                      <div style={{display: 'flex', gap: '0.5rem'}}>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedTrades(allTrades.map(t => t.id))}
                          style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem'}}
                        >
                          Alle
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setSelectedTrades([])}
                          style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem'}}
                        >
                          Keine
                        </button>
                      </div>
                    </div>

                    {selectedTrades.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.375rem',
                        marginBottom: '0.5rem',
                        padding: '0.5rem',
                        background: '#f0f9ff',
                        borderRadius: '0.5rem',
                        border: '1px solid #bae6fd'
                      }}>
                        {selectedTrades.map(tradeId => {
                          const trade = allTrades.find(t => t.id === tradeId);
                          if (!trade) return null;
                          return (
                            <span
                              key={trade.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.2rem 0.5rem',
                                background: '#006691',
                                color: '#fff',
                                borderRadius: '1rem',
                                fontSize: '0.75rem',
                                lineHeight: '1.4',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {trade.title}
                              <span
                                onClick={() => setSelectedTrades(prev => prev.filter(id => id !== trade.id))}
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '0.125rem',
                                  fontWeight: 'bold',
                                  fontSize: '0.85rem',
                                  lineHeight: '1',
                                  opacity: 0.8
                                }}
                                title="Entfernen"
                              >
                                &times;
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <div style={{position: 'relative'}}>
                      <input
                        type="text"
                        placeholder="Gewerk suchen..."
                        value={tradeSearchQuery}
                        onChange={(e) => {
                          setTradeSearchQuery(e.target.value);
                          setTradesDropdownOpen(true);
                        }}
                        onFocus={() => setTradesDropdownOpen(true)}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: tradesDropdownOpen ? '0.5rem 0.5rem 0 0' : '0.5rem',
                          fontSize: '0.875rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {tradesDropdownOpen && (
                        <>
                          <div
                            style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9}}
                            onClick={() => { setTradesDropdownOpen(false); setTradeSearchQuery(''); }}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #e5e7eb',
                            borderTop: 'none',
                            borderRadius: '0 0 0.5rem 0.5rem',
                            background: '#fff',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}>
                            {allTrades
                              .filter(t => t.title.toLowerCase().includes(tradeSearchQuery.toLowerCase()))
                              .map(trade => (
                                <label
                                  key={trade.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.4rem 0.75rem',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    background: selectedTrades.includes(trade.id) ? '#e0f2fe' : 'transparent',
                                    borderBottom: '1px solid #f3f4f6'
                                  }}
                                  onMouseEnter={(e) => { if (!selectedTrades.includes(trade.id)) e.currentTarget.style.background = '#f9fafb'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = selectedTrades.includes(trade.id) ? '#e0f2fe' : 'transparent'; }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedTrades.includes(trade.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedTrades(prev => [...prev, trade.id]);
                                      } else {
                                        setSelectedTrades(prev => prev.filter(id => id !== trade.id));
                                      }
                                    }}
                                  />
                                  {trade.title}
                                </label>
                              ))
                            }
                            {allTrades.filter(t => t.title.toLowerCase().includes(tradeSearchQuery.toLowerCase())).length === 0 && (
                              <div style={{padding: '0.75rem', fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center'}}>
                                Kein Gewerk gefunden
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {tradesLoading && (
                      <p style={{fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem'}}>Gewerke werden geladen...</p>
                    )}
                  </div>
                )}
              </div>

              <div style={{marginBottom: '1.5rem'}}>
              <h3 style={{marginBottom: '0.75rem'}}>E-Mail-Modus</h3>
              <div style={{display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem'}}>
                <button 
                  className={`btn ${emailMode === 'production' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => {
                    setEmailMode('production');
                    setSelectedContacts({});
                    setExpandedCompanies(new Set());
                    setSelectedTestRecipients([]);
                  }}
                  style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'}}
                >
                  <Briefcase size={18} />
                  Echte Empfänger (Unternehmen)
                </button>
                <button 
                  className={`btn ${emailMode === 'test' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={async () => {
                    setEmailMode('test');
                    setSelectedContacts({});
                    setExpandedCompanies(new Set());
                    setSelectedTestRecipients([]);
                    await loadTestRecipients();
                  }}
                  style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', background: emailMode === 'test' ? '#0ea5e9' : undefined}}
                >
                  <User size={18} />
                  Test-Modus (Kontaktpersonen)
                </button>
              </div>
              {emailMode === 'test' && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#e0f2fe',
                  border: '1px solid #0ea5e9',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}>
                  ℹ️ <strong>Test-Modus aktiv:</strong> Sie können Kontaktpersonen auswählen. Die E-Mail wird nur an diese Kontaktpersonen gesendet.
                </div>
              )}
            </div>

            <div className="filter-section" style={{marginBottom: '1.5rem'}}>
              <h3>Ansprechpartner für E-Mail-Signatur</h3>
              <div style={{marginTop: '1rem'}}>
                <label className="filter-label" style={{display: 'block', marginBottom: '0.5rem'}}>
                  Wählen Sie einen Ansprechpartner:
                </label>
                <select 
                  value={selectedContactPerson || ''}
                  onChange={(e) => setSelectedContactPerson(Number(e.target.value))}
                  className="filter-select"
                  style={{width: '100%', maxWidth: '400px', padding: '0.75rem'}}
                >
                  <option value="">Bitte wählen...</option>
                  {contactPersons.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.firstname} {person.lastname} - {person.position || 'Keine Funktion'}
                    </option>
                  ))}
                </select>
                {selectedContactPerson && contactPersons.find(p => p.id === selectedContactPerson) && (
                  <div style={{marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem', border: '1px solid #e5e7eb'}}>
                    <p style={{margin: 0, fontSize: '0.875rem', color: '#6b7280'}}>
                      <strong>Vorschau Signatur:</strong>
                    </p>
                    {(() => {
                      const person = contactPersons.find(p => p.id === selectedContactPerson);
                      return (
                        <div style={{marginTop: '0.5rem', fontSize: '0.875rem'}}>
                          <p style={{margin: 0}}>Beste Grüße</p>
                          <p style={{margin: 0}}>{person.firstname} {person.lastname}</p>
                          {person.position && <p style={{margin: 0}}>{person.position}</p>}
                          {person.phone && <p style={{margin: 0}}>Tel.: {person.phone}</p>}
                          <p style={{margin: 0}}>{person.email}</p>
                          <p style={{margin: 0}}>{person.website}</p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                <div>
                  <p className="section-description" style={{marginBottom: 0}}>
                    {emailMode === 'production' ? (
                      matchingCompanies.length > 0 ? (
                        <>
                          {matchingCompanies.length} Unternehmen gefunden
                          {filterType === 'radius' && ` im Umkreis von ${radiusFilter} km`}
                          {filterType === 'traveltime' && ` mit max. ${travelTimeFilter} Min Fahrtzeit`}
                          {selectedTrades.length > 0 && ` | ${selectedTrades.length} Gewerk(e) gefiltert`}
                          {getSelectedCompanyCount() > 0 && (
                            <span style={{marginLeft: '1rem', color: '#006691', fontWeight: '600'}}>
                              • {getSelectedCompanyCount()} Unternehmen / {getTotalSelectedContactCount()} Kontakte ausgewählt
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{color: '#9ca3af'}}>Bitte Filter anwenden, um passende Unternehmen zu laden.</span>
                      )
                    ) : (
                      <>
                        {testRecipients.length} Kontaktpersonen verfügbar
                        {selectedTestRecipients.length > 0 && (
                          <span style={{marginLeft: '1rem', color: '#0ea5e9', fontWeight: '600'}}>
                            • {selectedTestRecipients.length} ausgewählt
                          </span>
                        )}
                      </>
                    )}
                  </p>
                </div>
                {((emailMode === 'production' && getTotalSelectedContactCount() > 0) || (emailMode === 'test' && selectedTestRecipients.length > 0)) && (
                  <button 
                    className="btn btn-primary" 
                    onClick={generateEmail}
                    disabled={loading}
                    style={{display: 'flex', alignItems: 'center', gap: '0.5rem', background: emailMode === 'test' ? '#0ea5e9' : undefined}}
                  >
                    <Mail size={18} />
                    {emailMode === 'test' ? 'Test-E-Mail Vorschau' : resendCampaignData ? 'Bestehendes Mailing verwenden' : 'E-Mail Vorschau anzeigen'}
                  </button>
                )}
                {emailGenerationProgress && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: '#e0f2fe',
                    border: '1px solid #0ea5e9',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <RefreshCw size={18} className="spin" />
                    <span>{emailGenerationProgress}</span>
                  </div>
                )}
              </div>

              {emailMode === 'production' && matchingCompanies.length > 0 ? (
                <div>
                  {matchingCompanies.length > PAGE_SIZE && (
                    <div className="pagination-bar">
                      <span className="pagination-info">
                        Seite {currentPage} von {Math.ceil(matchingCompanies.length / PAGE_SIZE)} &middot; {matchingCompanies.length} Unternehmen
                      </span>
                      <div className="pagination-controls">
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                        >«</button>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(p => p - 1)}
                        >‹</button>
                        <span className="pagination-page">Seite {currentPage}</span>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage >= Math.ceil(matchingCompanies.length / PAGE_SIZE)}
                          onClick={() => setCurrentPage(p => p + 1)}
                        >›</button>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage >= Math.ceil(matchingCompanies.length / PAGE_SIZE)}
                          onClick={() => setCurrentPage(Math.ceil(matchingCompanies.length / PAGE_SIZE))}
                        >»</button>
                      </div>
                    </div>
                  )}
                  <div style={{overflowX: 'auto'}}>
                    <table className="companies-table">
                      <thead>
                        <tr>
                          <th style={{width: '36px'}}></th>
                          <th style={{width: '50px', textAlign: 'center'}}>
                            <input 
                              type="checkbox" 
                              onChange={(e) => {
                                if (e.target.checked) {
                                  selectAllCompanies(matchingCompanies);
                                } else {
                                  deselectAllCompanies();
                                }
                              }}
                              checked={getSelectedCompanyCount() === matchingCompanies.filter(c => (c.contacts || []).length > 0).length && matchingCompanies.filter(c => (c.contacts || []).length > 0).length > 0}
                            />
                          </th>
                          <th>Unternehmen</th>
                          <th>Ort</th>
                          <th>PLZ</th>
                          <th style={{textAlign: 'center'}}>Kontakte</th>
                          <th style={{textAlign: 'right'}}>Entfernung</th>
                          <th style={{textAlign: 'right'}}>Fahrzeit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchingCompanies
                          .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                          .map((company) => {
                          const companyContacts = company.contacts || [];
                          const selectedForCompany = selectedContacts[company.id] || [];
                          const isFullySelected = companyContacts.length > 0 && selectedForCompany.length === companyContacts.length;
                          const isPartiallySelected = selectedForCompany.length > 0 && !isFullySelected;
                          const isExpanded = expandedCompanies.has(company.id);
                          const hasContacts = companyContacts.length > 0;
                          return (
                            <React.Fragment key={company.id}>
                              <tr 
                                className={isFullySelected ? 'selected-row' : isPartiallySelected ? 'partial-row' : ''}
                                style={{cursor: 'pointer'}}
                                onClick={() => hasContacts && toggleCompanySelection(company)}
                              >
                                <td style={{textAlign: 'center', padding: '0.5rem'}} onClick={(e) => { e.stopPropagation(); if (hasContacts) toggleExpandCompany(company.id); }}>
                                  {hasContacts && (
                                    isExpanded ? <ChevronDown size={16} style={{color: '#006691'}} /> : <ChevronRight size={16} style={{color: '#9ca3af'}} />
                                  )}
                                </td>
                                <td style={{textAlign: 'center'}} onClick={(e) => e.stopPropagation()}>
                                  {hasContacts ? (
                                    <input 
                                      type="checkbox"
                                      checked={isFullySelected}
                                      ref={el => { if (el) el.indeterminate = isPartiallySelected; }}
                                      onChange={() => toggleCompanySelection(company)}
                                    />
                                  ) : (
                                    <input type="checkbox" disabled style={{opacity: 0.3}} />
                                  )}
                                </td>
                                <td>
                                  <strong>{company.name || 'Ohne Namen'}</strong>
                                  {company.description && (
                                    <div style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem'}}>
                                      {company.description.length > 80 ? company.description.substring(0, 80) + '...' : company.description}
                                    </div>
                                  )}
                                </td>
                                <td>{company.city || '-'}</td>
                                <td>{company.zipcode || '-'}</td>
                                <td style={{textAlign: 'center'}}>
                                  {hasContacts ? (
                                    <span 
                                      className="contact-count-badge"
                                      onClick={(e) => { e.stopPropagation(); toggleExpandCompany(company.id); }}
                                      style={{cursor: 'pointer'}}
                                    >
                                      <Users size={13} style={{marginRight: '0.25rem', verticalAlign: 'middle'}} />
                                      {selectedForCompany.length > 0 
                                        ? `${selectedForCompany.length}/${companyContacts.length}`
                                        : companyContacts.length
                                      }
                                    </span>
                                  ) : (
                                    <span style={{color: '#9ca3af'}}>0</span>
                                  )}
                                </td>
                                <td style={{textAlign: 'right', fontWeight: '600', color: '#006691'}}>
                                  {company.routeDistanceKm 
                                    ? `${company.routeDistanceKm} km` 
                                    : company.distanz ? `~${company.distanz.toFixed(1)} km` : '-'}
                                </td>
                                <td style={{textAlign: 'right', color: '#374151'}}>
                                  {company.routeDurationText || '-'}
                                </td>
                              </tr>
                              {isExpanded && companyContacts.map((contact) => {
                                const isContactSelected = selectedForCompany.includes(contact.id);
                                return (
                                  <tr
                                    key={`${company.id}-${contact.id}`}
                                    className={`contact-row ${isContactSelected ? 'contact-row--selected' : ''}`}
                                    style={{cursor: 'pointer'}}
                                    onClick={() => toggleContactSelection(company.id, contact.id)}
                                  >
                                    <td></td>
                                    <td style={{textAlign: 'center'}} onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={isContactSelected}
                                        onChange={() => toggleContactSelection(company.id, contact.id)}
                                      />
                                    </td>
                                    <td colSpan={3} style={{paddingLeft: '2rem'}}>
                                      <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                                        <User size={14} style={{color: '#6b7280', flexShrink: 0}} />
                                        <div>
                                          <span style={{fontWeight: '500'}}>
                                            {contact.firstname || ''} {contact.lastname || ''}
                                          </span>
                                          {contact.email && (
                                            <span style={{marginLeft: '0.75rem', color: '#6b7280', fontSize: '0.875rem'}}>
                                              {contact.email}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {matchingCompanies.length > PAGE_SIZE && (
                    <div className="pagination-bar" style={{marginTop: '0.5rem'}}>
                      <span className="pagination-info">
                        Zeige {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, matchingCompanies.length)} von {matchingCompanies.length}
                      </span>
                      <div className="pagination-controls">
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(1)}
                        >«</button>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(p => p - 1)}
                        >‹</button>
                        <span className="pagination-page">Seite {currentPage} / {Math.ceil(matchingCompanies.length / PAGE_SIZE)}</span>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage >= Math.ceil(matchingCompanies.length / PAGE_SIZE)}
                          onClick={() => setCurrentPage(p => p + 1)}
                        >›</button>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={currentPage >= Math.ceil(matchingCompanies.length / PAGE_SIZE)}
                          onClick={() => setCurrentPage(Math.ceil(matchingCompanies.length / PAGE_SIZE))}
                        >»</button>
                      </div>
                    </div>
                  )}
                </div>
              ) : emailMode === 'test' && testRecipients.length > 0 ? (
                <div style={{overflowX: 'auto'}}>
                  <table className="companies-table">
                    <thead>
                      <tr>
                        <th style={{width: '50px', textAlign: 'center'}}>
                          <input 
                            type="checkbox" 
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTestRecipients(testRecipients.map(r => r.sourceId));
                              } else {
                                setSelectedTestRecipients([]);
                              }
                            }}
                            checked={selectedTestRecipients.length === testRecipients.length && testRecipients.length > 0}
                          />
                        </th>
                        <th>Name</th>
                        <th>E-Mail</th>
                        <th>Firma/Position</th>
                        <th>Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testRecipients.map((recipient) => {
                        const isSelected = selectedTestRecipients.includes(recipient.sourceId);
                        return (
                          <tr 
                            key={recipient.id}
                            className={isSelected ? 'selected-row' : ''}
                            style={{cursor: 'pointer'}}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedTestRecipients(selectedTestRecipients.filter(id => id !== recipient.sourceId));
                              } else {
                                setSelectedTestRecipients([...selectedTestRecipients, recipient.sourceId]);
                              }
                            }}
                          >
                            <td style={{textAlign: 'center'}} onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  if (isSelected) {
                                    setSelectedTestRecipients(selectedTestRecipients.filter(id => id !== recipient.sourceId));
                                  } else {
                                    setSelectedTestRecipients([...selectedTestRecipients, recipient.sourceId]);
                                  }
                                }}
                              />
                            </td>
                            <td>
                              <strong>{recipient.firstname} {recipient.lastname}</strong>
                            </td>
                            <td>{recipient.email}</td>
                            <td>{recipient.company || '-'}</td>
                            <td>
                              <span style={{
                                background: '#dbeafe',
                                color: '#1e40af',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {recipient.company || 'Kontaktperson'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="no-data">
                  {loading ? 'Laden...' : emailMode === 'production' ? 'Keine passenden Unternehmen gefunden' : 'Keine Kontaktpersonen verfügbar'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderTrackingOverview = () => {
    const formatDate = (dateStr) => {
      if (!dateStr) return null;
      return new Date(dateStr).toLocaleString('de-DE', { 
        day: '2-digit', month: '2-digit', year: 'numeric', 
        hour: '2-digit', minute: '2-digit' 
      });
    };

    const RecipientTable = ({ recipients, isTest }) => {
      if (recipients.length === 0) {
        return <p className="no-data" style={{padding: '1rem', fontSize: '0.875rem'}}>Keine Einträge vorhanden</p>;
      }
      const clicked = recipients.filter(r => r.click_count > 0).length;
      return (
        <div>
          <div className="tracking-stats-row">
            <span className="tracking-stat">
              <Mail size={14} /> {recipients.length} versendet
            </span>
            <span className={`tracking-stat ${clicked > 0 ? 'tracking-stat--clicked' : 'tracking-stat--pending'}`}>
              <MousePointerClick size={14} /> {clicked} geklickt ({recipients.length > 0 ? Math.round((clicked / recipients.length) * 100) : 0}%)
            </span>
          </div>
          <div className="table-container" style={{marginTop: '0.75rem'}}>
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>E-Mail</th>
                  <th>Versendet am</th>
                  <th>Klicks</th>
                  <th>Erstmals geklickt</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r, idx) => (
                  <tr key={idx}>
                    <td>
                      {r.click_count > 0 ? (
                        <span className="tracking-badge tracking-badge--clicked">
                          <CheckCircle2 size={13} /> Geklickt
                        </span>
                      ) : (
                        <span className="tracking-badge tracking-badge--pending">
                          <Clock size={13} /> Ausstehend
                        </span>
                      )}
                    </td>
                    <td style={{fontWeight: '500'}}>{r.recipient_email}</td>
                    <td style={{color: '#6b7280', fontSize: '0.875rem'}}>{formatDate(r.created_at)}</td>
                    <td style={{textAlign: 'center', fontWeight: '600', color: r.click_count > 0 ? '#10b981' : '#9ca3af'}}>
                      {r.click_count}
                    </td>
                    <td style={{color: '#6b7280', fontSize: '0.875rem'}}>
                      {r.clicked_at ? formatDate(r.clicked_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className="mailing-view">
        <div className="view-header">
          <h2>Link-Tracking Übersicht</h2>
          <button className="btn btn-secondary" onClick={loadTrackingOverview} disabled={trackingLoading}>
            <RefreshCw size={18} className={trackingLoading ? 'spin' : ''} />
            {trackingLoading ? 'Laden...' : 'Aktualisieren'}
          </button>
        </div>

        {trackingData.length === 0 && !trackingLoading && (
          <p className="no-data">
            Noch keine Tracking-Daten vorhanden. Sende zuerst E-Mails über das E-Mailing-Modul.
          </p>
        )}

        {trackingData.map((project) => {
          const totalProduction = project.production.length;
          const totalTest = project.test.length;
          const clickedProduction = project.production.filter(r => r.click_count > 0).length;
          const clickedTest = project.test.filter(r => r.click_count > 0).length;

          return (
            <div key={project.project_id} className="tracking-project-card">
              <div className="tracking-project-header">
                <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                  <Briefcase size={20} color="#006691" />
                  <div>
                    <h3 style={{margin: 0, fontSize: '1.125rem', fontWeight: '700'}}>
                      Auftrag #{project.project_id}
                    </h3>
                    <a 
                      href={project.target_url} 
                      target="_blank" 
                      rel="noreferrer"
                      style={{fontSize: '0.8125rem', color: '#006691'}}
                    >
                      {project.target_url}
                    </a>
                  </div>
                </div>
                <div className="tracking-summary-chips">
                  <span className="tracking-chip tracking-chip--production">
                    <Briefcase size={13} />
                    Produktion: {clickedProduction}/{totalProduction} geklickt
                  </span>
                  <span className="tracking-chip tracking-chip--test">
                    <User size={13} />
                    Test: {clickedTest}/{totalTest} geklickt
                  </span>
                </div>
              </div>

              <div className="tracking-sections">
                <div className="tracking-section">
                  <div className="tracking-section-title tracking-section-title--production">
                    <Briefcase size={16} /> Echte Empfänger ({totalProduction})
                  </div>
                  <RecipientTable recipients={project.production} isTest={false} />
                </div>

                <div className="tracking-section">
                  <div className="tracking-section-title tracking-section-title--test">
                    <User size={16} /> Test-Empfänger ({totalTest})
                  </div>
                  <RecipientTable recipients={project.test} isTest={true} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <div className="mailing-view">
        <div className="view-header">
          <h2>Einstellungen</h2>
        </div>

        <div className="settings-container" style={{padding: '1rem'}}>
          {/* LLM Prompt Editor */}
          <div className="settings-section" style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem'}}>
              <Code size={24} color="#212A3E" />
              <h3 style={{margin: 0, color: '#212A3E'}}>LLM Prompt für E-Mail-Generierung</h3>
              {llmPromptIsDefault && (
                <span style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  Standard
                </span>
              )}
            </div>
            
            <p style={{color: '#6b7280', marginBottom: '1rem', fontSize: '0.875rem'}}>
              Hier können Sie den Prompt anpassen, der für die KI-gestützte E-Mail-Generierung verwendet wird. 
              Änderungen wirken sich sofort auf neue E-Mails aus.
            </p>

            {llmPromptLoading ? (
              <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                <RefreshCw size={24} className="spin" style={{marginBottom: '0.5rem'}} />
                <p>Prompt wird geladen...</p>
              </div>
            ) : (
              <>
                <textarea
                  value={llmPrompt}
                  onChange={(e) => setLlmPrompt(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '400px',
                    padding: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    background: '#f9fafb'
                  }}
                  placeholder="Prompt wird geladen..."
                />
                
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    className="btn btn-primary"
                    onClick={saveLlmPrompt}
                    disabled={llmPromptSaving || !llmPrompt.trim()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: llmPromptSaving || !llmPrompt.trim() ? 0.6 : 1
                    }}
                  >
                    {llmPromptSaving ? (
                      <><RefreshCw size={18} className="spin" /> Speichern...</>
                    ) : (
                      <><Save size={18} /> Prompt speichern</>
                    )}
                  </button>
                  
                  <button
                    className="btn btn-secondary"
                    onClick={resetLlmPrompt}
                    disabled={llmPromptSaving}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RefreshCw size={18} />
                    Auf Standard zurücksetzen
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Betreff-Prompt Editor */}
          <div className="settings-section" style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem'}}>
              <Code size={24} color="#212A3E" />
              <h3 style={{margin: 0, color: '#212A3E'}}>LLM Prompt für Betreffzeile</h3>
              {subjectPromptIsDefault && (
                <span style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  Standard
                </span>
              )}
            </div>
            
            <p style={{color: '#6b7280', marginBottom: '1rem', fontSize: '0.875rem'}}>
              Dieser Prompt steuert die KI-generierte Betreffzeile der E-Mails.
            </p>

            {subjectPromptLoading ? (
              <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                <RefreshCw size={24} className="spin" style={{marginBottom: '0.5rem'}} />
                <p>Prompt wird geladen...</p>
              </div>
            ) : (
              <>
                <textarea
                  value={subjectPrompt}
                  onChange={(e) => setSubjectPrompt(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    background: '#f9fafb'
                  }}
                  placeholder="Prompt wird geladen..."
                />
                
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    className="btn btn-primary"
                    onClick={saveSubjectPrompt}
                    disabled={subjectPromptSaving || !subjectPrompt.trim()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: subjectPromptSaving || !subjectPrompt.trim() ? 0.6 : 1
                    }}
                  >
                    {subjectPromptSaving ? (
                      <><RefreshCw size={18} className="spin" /> Speichern...</>
                    ) : (
                      <><Save size={18} /> Prompt speichern</>
                    )}
                  </button>
                  
                  <button
                    className="btn btn-secondary"
                    onClick={resetSubjectPrompt}
                    disabled={subjectPromptSaving}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RefreshCw size={18} />
                    Auf Standard zurücksetzen
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Vorschautext-Prompt Editor */}
          <div className="settings-section" style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            marginBottom: '2rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem'}}>
              <Code size={24} color="#212A3E" />
              <h3 style={{margin: 0, color: '#212A3E'}}>LLM Prompt für Vorschautext</h3>
              {previewPromptIsDefault && (
                <span style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  Standard
                </span>
              )}
            </div>
            
            <p style={{color: '#6b7280', marginBottom: '1rem', fontSize: '0.875rem'}}>
              Dieser Prompt steuert den KI-generierten Vorschautext (Preheader), der neben der Betreffzeile angezeigt wird.
            </p>

            {previewPromptLoading ? (
              <div style={{textAlign: 'center', padding: '2rem', color: '#6b7280'}}>
                <RefreshCw size={24} className="spin" style={{marginBottom: '0.5rem'}} />
                <p>Prompt wird geladen...</p>
              </div>
            ) : (
              <>
                <textarea
                  value={previewPrompt}
                  onChange={(e) => setPreviewPrompt(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '1rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    background: '#f9fafb'
                  }}
                  placeholder="Prompt wird geladen..."
                />
                
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '1rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    className="btn btn-primary"
                    onClick={savePreviewPrompt}
                    disabled={previewPromptSaving || !previewPrompt.trim()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      opacity: previewPromptSaving || !previewPrompt.trim() ? 0.6 : 1
                    }}
                  >
                    {previewPromptSaving ? (
                      <><RefreshCw size={18} className="spin" /> Speichern...</>
                    ) : (
                      <><Save size={18} /> Prompt speichern</>
                    )}
                  </button>
                  
                  <button
                    className="btn btn-secondary"
                    onClick={resetPreviewPrompt}
                    disabled={previewPromptSaving}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <RefreshCw size={18} />
                    Auf Standard zurücksetzen
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContactManagement = () => (
    <div className="mailing-view">
      <div className="view-header">
        <h2>Kontaktpersonen verwalten</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => setEditingContact({})}
          style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
        >
          <Plus size={18} />
          Neue Kontaktperson
        </button>
      </div>

      {editingContact !== null && (
        <div className="email-preview-section" style={{marginBottom: '2rem'}}>
          <h3>{editingContact.id ? 'Kontaktperson bearbeiten' : 'Neue Kontaktperson'}</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            saveContactPerson({
              firstname: formData.get('firstname'),
              lastname: formData.get('lastname'),
              position: formData.get('position'),
              phone: formData.get('phone'),
              email: formData.get('email'),
              website: formData.get('website')
            });
          }}>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem'}}>
              <div>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Vorname *</label>
                <input 
                  type="text" 
                  name="firstname" 
                  defaultValue={editingContact.firstname || ''}
                  required
                  style={{width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '0.5rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Nachname *</label>
                <input 
                  type="text" 
                  name="lastname" 
                  defaultValue={editingContact.lastname || ''}
                  required
                  style={{width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '0.5rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Funktion</label>
                <input 
                  type="text" 
                  name="position" 
                  defaultValue={editingContact.position || ''}
                  style={{width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '0.5rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Telefon</label>
                <input 
                  type="tel" 
                  name="phone" 
                  defaultValue={editingContact.phone || ''}
                  style={{width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '0.5rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>E-Mail *</label>
                <input 
                  type="email" 
                  name="email" 
                  defaultValue={editingContact.email || ''}
                  required
                  style={{width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '0.5rem'}}
                />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600'}}>Webseite</label>
                <input 
                  type="text" 
                  name="website" 
                  defaultValue={editingContact.website || 'www.handwerkconnected.de'}
                  style={{width: '100%', padding: '0.75rem', border: '2px solid #e5e7eb', borderRadius: '0.5rem'}}
                />
              </div>
            </div>
            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                <Save size={18} />
                Speichern
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditingContact(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="orders-list">
        <h3>Kontaktpersonen ({contactPersons.length})</h3>
        {contactPersons.length > 0 ? (
          <div style={{display: 'grid', gap: '1rem'}}>
            {contactPersons.map((person) => (
              <div key={person.id} className="company-card" style={{padding: '1.5rem'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                  <div style={{flex: 1}}>
                    <h4 style={{margin: '0 0 0.5rem 0', fontSize: '1.25rem'}}>
                      {person.firstname} {person.lastname}
                    </h4>
                    {person.position && (
                      <p style={{color: '#6b7280', margin: '0 0 0.75rem 0'}}>{person.position}</p>
                    )}
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                      {person.phone && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem'}}>
                          <strong>Tel:</strong> {person.phone}
                        </div>
                      )}
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem'}}>
                        <strong>E-Mail:</strong> {person.email}
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9375rem'}}>
                        <strong>Web:</strong> {person.website}
                      </div>
                    </div>
                  </div>
                  <div style={{display: 'flex', gap: '0.5rem'}}>
                    <button 
                      className="btn-icon" 
                      onClick={() => setEditingContact(person)}
                      title="Bearbeiten"
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      className="btn-icon" 
                      onClick={() => deleteContactPerson(person.id)}
                      title="Löschen"
                      style={{color: '#dc2626'}}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">Keine Kontaktpersonen vorhanden</p>
        )}
      </div>
    </div>
  );

  const renderTables = () => {
    const filteredTables = tables.filter(table => {
      const tableName = Object.values(table)[0];
      return tableName.toLowerCase().includes(tableFilter.toLowerCase());
    });

    return (
      <div className="tables-view">
        <div className="view-header">
          <h2>Datenbank Tabellen</h2>
          <div className="header-actions">
            <input 
              type="text"
              placeholder="Tabellen filtern..."
              value={tableFilter}
              onChange={(e) => setTableFilter(e.target.value)}
              className="filter-input"
            />
            <button className="btn btn-secondary" onClick={loadTables} disabled={loading}>
              <RefreshCw size={18} />
              Aktualisieren
            </button>
          </div>
        </div>

        {filteredTables.length > 0 ? (
          <div className="tables-list">
          {filteredTables.map((table, index) => {
            const tableName = Object.values(table)[0];
            return (
              <div 
                key={index} 
                className={`table-item ${selectedTable === tableName ? 'active' : ''}`}
              >
                <div className="table-item-content" onClick={() => loadTableData(tableName)}>
                  <Table size={20} />
                  <span>{tableName}</span>
                </div>
                <button 
                  className="btn-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    loadTableInfo(tableName);
                    setActiveView('schema');
                  }}
                  title="Detaillierte Infos"
                >
                  <Info size={16} />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="no-data">Keine Tabellen gefunden oder noch nicht geladen.</p>
      )}

      {tableData.length > 0 && (
        <div className="table-data">
          <div className="table-data-header">
            <h3>Daten aus: {selectedTable}</h3>
            <div className="column-controls">
              <button className="btn btn-small" onClick={() => toggleAllColumns(selectedTable, true)}>
                Alle anzeigen
              </button>
              <button className="btn btn-small" onClick={() => toggleAllColumns(selectedTable, false)}>
                Alle ausblenden
              </button>
            </div>
          </div>
          
          <div className="column-toggles">
            {Object.keys(tableData[0]).map((col) => (
              <label key={col} className="column-toggle">
                <input 
                  type="checkbox"
                  checked={visibleColumns[selectedTable]?.[col] !== false}
                  onChange={() => toggleColumn(selectedTable, col)}
                />
                <span>{col}</span>
              </label>
            ))}
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {Object.keys(tableData[0])
                    .filter(key => visibleColumns[selectedTable]?.[key] !== false)
                    .map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index}>
                    {Object.keys(row)
                      .filter(key => visibleColumns[selectedTable]?.[key] !== false)
                      .map((key, idx) => (
                        <td key={idx}>{String(row[key])}</td>
                      ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="data-count">{tableData.length} Einträge angezeigt</p>
        </div>
      )}
    </div>
    );
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="login-container">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '1rem',
          color: 'white'
        }}>
          <div className="spin" style={{ fontSize: '2rem' }}>⏳</div>
          <p>Lade...</p>
        </div>
      </div>
    );
  }

  // Show LoginPage if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="app">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Activity size={32} />
            <h1>Handwerk Connected Marketing-Dashboard</h1>
          </div>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {user && (
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white' }}>
                <User size={18} />
                <span>{user.username}</span>
              </div>
            )}
            <button 
              className="logout-btn" 
              onClick={logout}
              title="Abmelden"
              style={{ 
                background: 'rgba(255,255,255,0.1)', 
                border: 'none', 
                color: 'white', 
                padding: '8px', 
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <LogOut size={20} />
            </button>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <div className="app-container">
        <nav className={`sidebar ${menuOpen ? 'open' : ''}`}>
          <ul className="nav-menu">
            <li 
              className={activeView === 'dashboard' ? 'active' : ''}
              onClick={() => {
                setActiveView('dashboard');
                setMenuOpen(false);
              }}
            >
              <Activity size={20} />
              <span>Dashboard</span>
            </li>

            <li 
              className={activeView === 'mailing' ? 'active' : ''}
              onClick={() => {
                setActiveView('mailing');
                setMenuOpen(false);
              }}
            >
              <Mail size={18} />
              <span>E-Mailing</span>
            </li>
            <li 
              className={activeView === 'tracking' ? 'active' : ''}
              onClick={() => {
                setActiveView('tracking');
                setMenuOpen(false);
                loadTrackingOverview();
              }}
            >
              <BarChart2 size={18} />
              <span>Link-Tracking</span>
            </li>
            <li 
              className={activeView === 'contacts' ? 'active' : ''}
              onClick={() => {
                setActiveView('contacts');
                setMenuOpen(false);
              }}
            >
              <User size={18} />
              <span>Kontaktpersonen</span>
            </li>
            <li 
              className={activeView === 'settings' ? 'active' : ''}
              onClick={() => {
                setActiveView('settings');
                setMenuOpen(false);
              }}
            >
              <Settings size={18} />
              <span>Einstellungen</span>
            </li>
          </ul>
        </nav>

        <main className="main-content">
          {activeView === 'dashboard' && renderDashboard()}
          {activeView === 'tables' && renderTables()}
          {activeView === 'relationships' && renderRelationships()}
          {activeView === 'mailing' && renderEmailMailing()}
          {activeView === 'tracking' && renderTrackingOverview()}
          {activeView === 'contacts' && renderContactManagement()}
          {activeView === 'settings' && renderSettings()}
          {activeView === 'query' && (
            <div className="query-editor-view">
              <h2>SQL Query-Editor</h2>
              
              <div className="query-editor-container">
                <div className="editor-header">
                  <h3>Query eingeben</h3>
                  <div className="editor-actions">
                    <button className="btn btn-small btn-secondary" onClick={saveQuery}>
                      <Save size={16} />
                      Speichern
                    </button>
                    <button className="btn btn-small btn-primary" onClick={executeQuery} disabled={loading}>
                      <Play size={16} />
                      {loading ? 'Ausführen...' : 'Ausführen'}
                    </button>
                  </div>
                </div>
                
                <textarea
                  className="query-textarea"
                  value={queryEditor}
                  onChange={(e) => setQueryEditor(e.target.value)}
                  placeholder="SELECT * FROM tabelle1 t1\nJOIN tabelle2 t2 ON t1.id = t2.tabelle1_id\nWHERE t1.status = 'aktiv'"
                  rows={10}
                />

                <div className="query-hints">
                  <h4>Beispiel-Queries:</h4>
                  <div className="hint-buttons">
                    <button 
                      className="hint-btn"
                      onClick={() => setQueryEditor('SELECT * FROM ' + (tables[0] ? Object.values(tables[0])[0] : 'table'))}
                    >
                      Einfache Abfrage
                    </button>
                    <button 
                      className="hint-btn"
                      onClick={() => setQueryEditor('SELECT t1.*, t2.*\nFROM table1 t1\nJOIN table2 t2 ON t1.id = t2.table1_id\nLIMIT 100')}
                    >
                      JOIN Abfrage
                    </button>
                    <button 
                      className="hint-btn"
                      onClick={() => setQueryEditor('SELECT column, COUNT(*) as count\nFROM table\nGROUP BY column\nORDER BY count DESC')}
                    >
                      GROUP BY
                    </button>
                  </div>
                </div>
              </div>

              {savedQueries.length > 0 && (
                <div className="saved-queries">
                  <h3>Gespeicherte Queries ({savedQueries.length})</h3>
                  <div className="saved-queries-list">
                    {savedQueries.map((sq, idx) => (
                      <div key={idx} className="saved-query-item">
                        <div className="saved-query-info">
                          <strong>{sq.name}</strong>
                          <span className="saved-query-date">
                            {new Date(sq.date).toLocaleString('de-DE')}
                          </span>
                        </div>
                        <div className="saved-query-actions">
                          <button 
                            className="btn-icon"
                            onClick={() => loadQuery(sq.query)}
                            title="Laden"
                          >
                            <Code size={16} />
                          </button>
                          <button 
                            className="btn-icon"
                            onClick={() => deleteQuery(idx)}
                            title="Löschen"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {queryResults.length > 0 && (
                <div className="query-results">
                  <h3>Ergebnisse ({queryResults.length} Zeilen)</h3>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(queryResults[0]).map((key) => (
                            <th key={key}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queryResults.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, idx) => (
                              <td key={idx}>{String(value)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeView === 'schema' && (
            <div className="schema-view">
              <h2>Tabellendetails</h2>
              {tableInfo ? (
                <div className="schema-details">
                  <div className="schema-header">
                    <h3>{tableInfo.table}</h3>
                    <span className="row-count">{tableInfo.rowCount} Einträge</span>
                  </div>

                  <div className="schema-section">
                    <h4>Spalten ({tableInfo.columns.length})</h4>
                    <div className="columns-grid">
                      {tableInfo.columns.map((col, idx) => (
                        <div key={idx} className="column-card">
                          <div className="column-name">
                            {col.COLUMN_KEY === 'PRI' && <Key size={14} color="#f59e0b" />}
                            {col.COLUMN_NAME}
                          </div>
                          <div className="column-type">{col.DATA_TYPE}</div>
                          <div className="column-meta">
                            {col.IS_NULLABLE === 'NO' && <span className="badge">NOT NULL</span>}
                            {col.EXTRA && <span className="badge">{col.EXTRA}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {tableInfo.foreignKeys.length > 0 && (
                    <div className="schema-section">
                      <h4>Foreign Keys ({tableInfo.foreignKeys.length})</h4>
                      <div className="fk-list">
                        {tableInfo.foreignKeys.map((fk, idx) => (
                          <div key={idx} className="fk-item">
                            <Key size={16} />
                            <span>{fk.COLUMN_NAME}</span>
                            <ArrowRight size={14} />
                            <span className="fk-target">
                              {fk.REFERENCED_TABLE_NAME}.{fk.REFERENCED_COLUMN_NAME}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tableInfo.referencedBy.length > 0 && (
                    <div className="schema-section">
                      <h4>Referenziert von ({tableInfo.referencedBy.length})</h4>
                      <div className="ref-list">
                        {tableInfo.referencedBy.map((ref, idx) => (
                          <div key={idx} className="ref-item">
                            <Database size={16} />
                            <span>{ref.referencingTable}</span>
                            <span className="ref-col">({ref.referencingColumn})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="no-data">Wählen Sie eine Tabelle aus, um Details anzuzeigen</p>
              )}
            </div>
          )}
        </main>
      </div>
      
      <footer className="app-footer">
        <div className="footer-content">
          <span className="version-info">
            Version {packageJson.version} | Build #{packageJson.buildNumber}
          </span>
          <span className="db-status-info" style={{marginLeft: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '1rem', fontSize: '0.8rem'}}>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.4rem'}}>
              {dbStatus ? (
                dbStatus.success ? (
                  <><CheckCircle size={14} color="#10b981" /> App-DB: {dbStatus.database || 'Verbunden'}</>
                ) : (
                  <><XCircle size={14} color="#ef4444" /> App-DB: Fehler</>
                )
              ) : (
                <><Database size={14} color="#9ca3af" /> App-DB: Prüfe...</>
              )}
            </span>
            <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.4rem'}}>
              {mailingDbStatus ? (
                mailingDbStatus.success ? (
                  <><CheckCircle size={14} color="#10b981" /> Mailing-DB: {mailingDbStatus.database || 'Verbunden'}</>
                ) : (
                  <><XCircle size={14} color="#ef4444" /> Mailing-DB: Fehler</>
                )
              ) : (
                <><Database size={14} color="#9ca3af" /> Mailing-DB: Prüfe...</>
              )}
            </span>
            <button 
              onClick={() => checkDatabaseStatus(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'inline-flex',
                alignItems: 'center',
                color: '#6b7280',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#006691'}
              onMouseLeave={(e) => e.target.style.color = '#6b7280'}
              title="Datenbankverbindungen neu prüfen"
            >
              <RefreshCw size={14} />
            </button>
          </span>
        </div>
      </footer>

      {confirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{
              margin: '0 0 1rem 0',
              fontSize: '1.25rem',
              fontWeight: '700',
              color: confirmDialog.danger ? '#dc2626' : '#1f2937'
            }}>
              {confirmDialog.title}
            </h3>
            <p style={{
              margin: '0 0 1.5rem 0',
              color: '#6b7280',
              whiteSpace: 'pre-line',
              lineHeight: '1.6'
            }}>
              {confirmDialog.message}
            </p>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button 
                className="btn btn-secondary"
                onClick={confirmDialog.onCancel}
                style={{
                  padding: '0.625rem 1.25rem'
                }}
              >
                Abbrechen
              </button>
              <button 
                className="btn btn-primary"
                onClick={confirmDialog.onConfirm}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: confirmDialog.danger ? '#dc2626' : '#006691'
                }}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AppWrapper() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWrapper;
